/** @module @liquidefy/code-editor/types @version 3.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 RESULT & COMMON — EditorResult<T>, EditorEvent, Timestamp, Position, Range, Selection
// ═══════════════════════════════════════════════════════════════════════════════

export type Timestamp = number;

export interface Position {
  readonly line: number;
  readonly column: number;
}

export interface Range {
  readonly start: Position;
  readonly end: Position;
}

export interface Selection {
  readonly anchor: Position;
  readonly active: Position;
  readonly isReversed: boolean;
}

export type EditorEventDomain =
  | 'document'
  | 'cursor'
  | 'history'
  | 'marker'
  | 'tokenizer'
  | 'language'
  | 'completion'
  | 'diagnostic'
  | 'navigation'
  | 'presentation'
  | 'layout'
  | 'widget'
  | 'editor'
  | 'find'
  | 'snippet'
  | 'command'
  | 'accessibility'
  | 'terminal'
  | 'pty'
  | 'buffer'
  | 'ansi'
  | 'output'
  | 'session'
  | 'multiplexer'
  | 'recording'
  | 'collaboration'
  | 'cloud'
  | 'ai'
  | 'agent'
  | 'conversation'
  | 'codegen'
  | 'analysis'
  | 'model'
  | 'cache'
  | 'personalization'
  | 'security'
  | 'workspace'
  | 'git'
  | 'filesystem'
  | 'project'
  | 'plugin'
  | 'settings'
  | 'diff'
  | 'review'
  | 'adapter'
  | 'global'
  | 'io';

export type EditorEventKind =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'opened'
  | 'closed'
  | 'changed'
  | 'moved'
  | 'renamed'
  | 'activated'
  | 'deactivated'
  | 'started'
  | 'stopped'
  | 'paused'
  | 'resumed'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'connected'
  | 'disconnected'
  | 'synced'
  | 'conflicted'
  | 'resolved'
  | 'requested'
  | 'responded'
  | 'queued'
  | 'flushed'
  | 'expired';

export interface EditorEvent {
  readonly kind: EditorEventKind;
  readonly domain: EditorEventDomain;
  readonly timestamp: Timestamp;
  readonly payload?: unknown;
}

export type EditorResultSuccess<T> = {
  readonly success: true;
  readonly code: string;
  readonly message: string;
  readonly value: T;
  readonly events: EditorEvent[];
};

export type EditorResultFailure = {
  readonly success: false;
  readonly code: string;
  readonly message: string;
  readonly error?: unknown;
};

export type EditorResult<T> = EditorResultSuccess<T> | EditorResultFailure;

// ═══════════════════════════════════════════════════════════════════════════════
// §2 BRANDED IDS — every entity ID in the system
// ═══════════════════════════════════════════════════════════════════════════════

export type DocumentId = string & { readonly _brand: 'DocumentId' };
export type BufferId = string & { readonly _brand: 'BufferId' };
export type CursorGroupId = string & { readonly _brand: 'CursorGroupId' };
export type MarkerId = string & { readonly _brand: 'MarkerId' };
export type TokenId = string & { readonly _brand: 'TokenId' };
export type GrammarId = string & { readonly _brand: 'GrammarId' };
export type LanguageId = string & { readonly _brand: 'LanguageId' };
export type CompletionId = string & { readonly _brand: 'CompletionId' };
export type DiagnosticId = string & { readonly _brand: 'DiagnosticId' };
export type CodeActionId = string & { readonly _brand: 'CodeActionId' };
export type CodeLensId = string & { readonly _brand: 'CodeLensId' };
export type ViewportId = string & { readonly _brand: 'ViewportId' };
export type DecorationId = string & { readonly _brand: 'DecorationId' };
export type WidgetId = string & { readonly _brand: 'WidgetId' };
export type EditorInstanceId = string & { readonly _brand: 'EditorInstanceId' };
export type SnippetId = string & { readonly _brand: 'SnippetId' };
export type CommandId = string & { readonly _brand: 'CommandId' };
export type KeybindingId = string & { readonly _brand: 'KeybindingId' };
export type KeyContextId = string & { readonly _brand: 'KeyContextId' };
export type ChordId = string & { readonly _brand: 'ChordId' };
export type ThemeId = string & { readonly _brand: 'ThemeId' };
export type TokenTypeId = string & { readonly _brand: 'TokenTypeId' };
export type PaletteId = string & { readonly _brand: 'PaletteId' };
export type SearchSessionId = string & { readonly _brand: 'SearchSessionId' };
export type SearchScopeId = string & { readonly _brand: 'SearchScopeId' };
export type TerminalId = string & { readonly _brand: 'TerminalId' };
export type PTYProcessId = string & { readonly _brand: 'PTYProcessId' };
export type TerminalSessionId = string & { readonly _brand: 'TerminalSessionId' };
export type TerminalBufferId = string & { readonly _brand: 'TerminalBufferId' };
export type MultiplexSessionId = string & { readonly _brand: 'MultiplexSessionId' };
export type MultiplexWindowId = string & { readonly _brand: 'MultiplexWindowId' };
export type MultiplexPaneId = string & { readonly _brand: 'MultiplexPaneId' };
export type RecordingId = string & { readonly _brand: 'RecordingId' };
export type RecordingFrameId = string & { readonly _brand: 'RecordingFrameId' };
export type ShareSessionId = string & { readonly _brand: 'ShareSessionId' };
export type ParticipantId = string & { readonly _brand: 'ParticipantId' };
export type CloudTerminalId = string & { readonly _brand: 'CloudTerminalId' };
export type AIRequestId = string & { readonly _brand: 'AIRequestId' };
export type AIResponseId = string & { readonly _brand: 'AIResponseId' };
export type AIAgentId = string & { readonly _brand: 'AIAgentId' };
export type AIConversationId = string & { readonly _brand: 'AIConversationId' };
export type AIMessageId = string & { readonly _brand: 'AIMessageId' };
export type AIModelId = string & { readonly _brand: 'AIModelId' };
export type CacheEntryId = string & { readonly _brand: 'CacheEntryId' };
export type WorkspaceId = string & { readonly _brand: 'WorkspaceId' };
export type PanelId = string & { readonly _brand: 'PanelId' };
export type TabId = string & { readonly _brand: 'TabId' };
export type TabGroupId = string & { readonly _brand: 'TabGroupId' };
export type LayoutNodeId = string & { readonly _brand: 'LayoutNodeId' };
export type GitRepositoryId = string & { readonly _brand: 'GitRepositoryId' };
export type CommitHash = string & { readonly _brand: 'CommitHash' };
export type BranchName = string & { readonly _brand: 'BranchName' };
export type RemoteName = string & { readonly _brand: 'RemoteName' };
export type TagName = string & { readonly _brand: 'TagName' };
export type StashId = string & { readonly _brand: 'StashId' };
export type HookId = string & { readonly _brand: 'HookId' };
export type FileId = string & { readonly _brand: 'FileId' };
export type DirectoryId = string & { readonly _brand: 'DirectoryId' };
export type SearchResultId = string & { readonly _brand: 'SearchResultId' };
export type IndexId = string & { readonly _brand: 'IndexId' };
export type ProjectId = string & { readonly _brand: 'ProjectId' };
export type DependencyId = string & { readonly _brand: 'DependencyId' };
export type EnvironmentId = string & { readonly _brand: 'EnvironmentId' };
export type TaskId = string & { readonly _brand: 'TaskId' };
export type BuildId = string & { readonly _brand: 'BuildId' };
export type PluginId = string & { readonly _brand: 'PluginId' };
export type PluginManifestId = string & { readonly _brand: 'PluginManifestId' };
export type SettingsProfileId = string & { readonly _brand: 'SettingsProfileId' };
export type DiffId = string & { readonly _brand: 'DiffId' };
export type HunkId = string & { readonly _brand: 'HunkId' };
export type ReviewThreadId = string & { readonly _brand: 'ReviewThreadId' };
export type ReviewCommentId = string & { readonly _brand: 'ReviewCommentId' };
export type AdapterId = string & { readonly _brand: 'AdapterId' };
export type EventChannelId = string & { readonly _brand: 'EventChannelId' };
export type StorageNamespace = string & { readonly _brand: 'StorageNamespace' };
export type PermissionId = string & { readonly _brand: 'PermissionId' };
export type LogChannelId = string & { readonly _brand: 'LogChannelId' };

// Branded ID factory functions
export function asKeybindingId(s: string): KeybindingId { return s as KeybindingId; }
export function asKeyContextId(s: string): KeyContextId { return s as KeyContextId; }
export function asChordId(s: string): ChordId { return s as ChordId; }
export function asThemeId(s: string): ThemeId { return s as ThemeId; }
export function asTokenTypeId(s: string): TokenTypeId { return s as TokenTypeId; }
export function asPaletteId(s: string): PaletteId { return s as PaletteId; }
export function asSearchSessionId(s: string): SearchSessionId { return s as SearchSessionId; }
export function asSearchResultId(s: string): SearchResultId { return s as SearchResultId; }
export function asSearchScopeId(s: string): SearchScopeId { return s as SearchScopeId; }

// ═══════════════════════════════════════════════════════════════════════════════
// §3 DOCUMENT MODEL — document entity, content, edit operations, encoding
// ═══════════════════════════════════════════════════════════════════════════════

export type DocumentEncoding =
  | 'utf-8'
  | 'utf-16le'
  | 'utf-16be'
  | 'utf-32'
  | 'latin1'
  | 'shift-jis'
  | 'euc-jp'
  | 'iso-8859-1';

export type LineEnding = 'lf' | 'crlf' | 'cr';

export type ByteOrderMark = 'none' | 'utf-8' | 'utf-16le' | 'utf-16be';

export type EditOperationKind =
  | 'insert'
  | 'delete'
  | 'replace'
  | 'transpose'
  | 'indent'
  | 'dedent'
  | 'commentToggle'
  | 'bracketComplete';

export type EditOrigin =
  | 'user'
  | 'undo'
  | 'redo'
  | 'auto-format'
  | 'auto-indent'
  | 'auto-close'
  | 'snippet'
  | 'completion'
  | 'paste'
  | 'external'
  | 'collab'
  | 'ai';

export interface EditOperation {
  readonly kind: EditOperationKind;
  readonly range: Range;
  readonly text: string;
  readonly origin: EditOrigin;
}

export interface DocumentState {
  readonly id: DocumentId;
  readonly content: string;
  readonly language: LanguageId;
  readonly version: number;
  readonly encoding: DocumentEncoding;
  readonly lineEnding: LineEnding;
  readonly byteOrderMark: ByteOrderMark;
  readonly lineCount: number;
  readonly charCount: number;
  readonly isDirty: boolean;
  readonly isReadOnly: boolean;
  readonly eol: string;
}

export interface DocumentSnapshot {
  readonly id: string;
  readonly documentId: DocumentId;
  readonly version: number;
  readonly content: string;
  readonly timestamp: Timestamp;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 CURSOR & SELECTION — multi-cursor, column selection, cursor groups
// ═══════════════════════════════════════════════════════════════════════════════

export interface CursorState {
  readonly position: Position;
  readonly desiredColumn: number;
  readonly selectionAnchor?: Position;
}

export interface CursorGroup {
  readonly id: CursorGroupId;
  readonly cursors: CursorState[];
  readonly primaryIndex: number;
}

export type SelectionKind = 'normal' | 'line' | 'block' | 'word';

export interface ColumnSelection {
  readonly startLine: number;
  readonly endLine: number;
  readonly startColumn: number;
  readonly endColumn: number;
}

export type CursorStyle =
  | 'block'
  | 'underline'
  | 'bar'
  | 'block-outline'
  | 'underline-thin'
  | 'bar-thin';

// ═══════════════════════════════════════════════════════════════════════════════
// §5 HISTORY — undo/redo with branching
// ═══════════════════════════════════════════════════════════════════════════════

export interface HistoryEntry {
  readonly id: string;
  readonly operations: EditOperation[];
  readonly beforeCursors: CursorState[];
  readonly afterCursors: CursorState[];
  readonly timestamp: Timestamp;
  readonly groupId?: string;
}

export interface HistoryBranch {
  readonly id: string;
  readonly parentEntryId: string;
  readonly entries: HistoryEntry[];
}

export interface HistoryState {
  readonly entries: HistoryEntry[];
  readonly currentIndex: number;
  readonly branches: HistoryBranch[];
  readonly activeBranch: string;
}

export type UndoStopKind =
  | 'explicit'
  | 'timeout'
  | 'cursor-move'
  | 'content-type-change';

// ═══════════════════════════════════════════════════════════════════════════════
// §6 MARKERS — bookmarks, breakpoints, annotations, diagnostics
// ═══════════════════════════════════════════════════════════════════════════════

export type MarkerKind =
  | 'bookmark'
  | 'breakpoint'
  | 'conditional-breakpoint'
  | 'logpoint'
  | 'annotation'
  | 'diagnostic'
  | 'fold-region'
  | 'changed-line'
  | 'search-match'
  | 'linked-edit';

export type MarkerStickiness =
  | 'never-grows'
  | 'grows-only-when-typing-before'
  | 'grows-only-when-typing-after'
  | 'always-grows';

export type DiagnosticSeverity = 'error' | 'warning' | 'info' | 'hint';

export interface DiagnosticRelatedInfo {
  readonly uri: string;
  readonly range: Range;
  readonly message: string;
}

export interface DiagnosticQuickFix {
  readonly title: string;
  readonly edit: EditOperation[];
  readonly isPreferred: boolean;
}

export interface DiagnosticData {
  readonly severity: DiagnosticSeverity;
  readonly source: string;
  readonly code: string | number;
  readonly message: string;
  readonly relatedInfo: DiagnosticRelatedInfo[];
  readonly quickFixes: DiagnosticQuickFix[];
}

export interface MarkerState {
  readonly id: MarkerId;
  readonly kind: MarkerKind;
  readonly range: Range;
  readonly data: DiagnosticData | Record<string, unknown>;
  readonly stickiness: MarkerStickiness;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 TOKENIZER & SYNTAX — tokens, grammar, incremental tokenization
// ═══════════════════════════════════════════════════════════════════════════════

export type TokenType =
  | 'keyword'
  | 'identifier'
  | 'string'
  | 'number'
  | 'operator'
  | 'comment'
  | 'punctuation'
  | 'type'
  | 'decorator'
  | 'regex'
  | 'template'
  | 'embedded'
  | 'whitespace'
  | 'invalid';

export interface Token {
  readonly type: TokenType;
  readonly startOffset: number;
  readonly endOffset: number;
  readonly scopes: string[];
}

export interface TokenizerState {
  readonly ruleStack: string[];
  readonly languageId: LanguageId;
}

export interface TokenLine {
  readonly lineNumber: number;
  readonly tokens: Token[];
  readonly state: TokenizerState;
  readonly version: number;
}

export interface GrammarCaptures {
  readonly [captureId: string]: { readonly name: string };
}

export interface GrammarPattern {
  readonly match?: string;
  readonly begin?: string;
  readonly end?: string;
  readonly captures?: GrammarCaptures;
  readonly beginCaptures?: GrammarCaptures;
  readonly endCaptures?: GrammarCaptures;
  readonly name?: string;
  readonly contentName?: string;
  readonly patterns?: GrammarPattern[];
}

export interface GrammarRepository {
  readonly [ruleName: string]: GrammarPattern;
}

export interface GrammarDefinition {
  readonly id: GrammarId;
  readonly scopeName: string;
  readonly patterns: GrammarPattern[];
  readonly repository: GrammarRepository;
}

export interface ScopeResolution {
  readonly scopes: string[];
  readonly tokenType: TokenType;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 LANGUAGE REGISTRY — language detection, configuration
// ═══════════════════════════════════════════════════════════════════════════════

export interface CommentRule {
  readonly lineComment?: string;
  readonly blockComment?: readonly [string, string];
}

export interface BracketPair {
  readonly open: string;
  readonly close: string;
  readonly notIn?: string[];
}

export interface AutoClosingPair {
  readonly open: string;
  readonly close: string;
  readonly notIn?: string[];
}

export interface SurroundingPair {
  readonly open: string;
  readonly close: string;
}

export interface FoldingMarkers {
  readonly start: string;
  readonly end: string;
}

export interface FoldingRules {
  readonly offSide?: boolean;
  readonly markers?: FoldingMarkers;
}

export interface IndentationRules {
  readonly increaseIndentPattern: string;
  readonly decreaseIndentPattern: string;
  readonly indentNextLinePattern?: string;
  readonly unIndentedLinePattern?: string;
}

export interface OnEnterRule {
  readonly beforeText: string;
  readonly afterText?: string;
  readonly previousLineText?: string;
  readonly action: OnEnterAction;
}

export type OnEnterAction = {
  readonly indent: 'none' | 'indent' | 'outdent' | 'indentOutdent';
  readonly appendText?: string;
  readonly removeText?: number;
};

export interface LanguageConfiguration {
  readonly comments: CommentRule;
  readonly brackets: BracketPair[];
  readonly autoClosingPairs: AutoClosingPair[];
  readonly surroundingPairs: SurroundingPair[];
  readonly folding: FoldingRules;
  readonly indentation: IndentationRules;
  readonly wordPattern?: string;
  readonly onEnterRules?: OnEnterRule[];
}

export interface LanguageDefinition {
  readonly id: LanguageId;
  readonly name: string;
  readonly aliases: string[];
  readonly extensions: string[];
  readonly filenames: string[];
  readonly mimeTypes: string[];
  readonly firstLine?: string;
  readonly configuration: LanguageConfiguration;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 LANGUAGE SERVICES — completions, hover, diagnostics, actions
// ═══════════════════════════════════════════════════════════════════════════════

export type CompletionKind =
  | 'text'
  | 'method'
  | 'function'
  | 'constructor'
  | 'field'
  | 'variable'
  | 'class'
  | 'interface'
  | 'module'
  | 'property'
  | 'unit'
  | 'value'
  | 'enum'
  | 'keyword'
  | 'snippet'
  | 'color'
  | 'file'
  | 'reference'
  | 'folder'
  | 'enum-member'
  | 'constant'
  | 'struct'
  | 'event'
  | 'operator'
  | 'type-parameter';

export type InsertTextRule =
  | 'keep-whitespace'
  | 'insert-as-snippet';

export interface CompletionItem {
  readonly id: CompletionId;
  readonly label: string;
  readonly kind: CompletionKind;
  readonly detail?: string;
  readonly documentation?: string;
  readonly insertText: string;
  readonly insertTextRules?: InsertTextRule[];
  readonly range: Range;
  readonly sortText?: string;
  readonly filterText?: string;
  readonly preselect?: boolean;
  readonly commitCharacters?: string[];
  readonly additionalTextEdits?: EditOperation[];
  readonly command?: CommandData;
}

export type CompletionTriggerKind = 'invoke' | 'character' | 'incomplete';

export interface CompletionContext {
  readonly triggerKind: CompletionTriggerKind;
  readonly triggerCharacter?: string;
}

export interface HoverInfo {
  readonly contents: string[];
  readonly range?: Range;
}

export type DiagnosticTag = 'unnecessary' | 'deprecated';

export interface DiagnosticRelatedInformation {
  readonly uri: string;
  readonly range: Range;
  readonly message: string;
}

export interface DiagnosticInfo {
  readonly severity: DiagnosticSeverity;
  readonly range: Range;
  readonly message: string;
  readonly source?: string;
  readonly code?: string | number;
  readonly relatedInformation?: DiagnosticRelatedInformation[];
  readonly tags?: DiagnosticTag[];
}

export type CodeActionKind =
  | 'quickfix'
  | 'refactor'
  | 'refactor.extract'
  | 'refactor.inline'
  | 'refactor.rewrite'
  | 'source'
  | 'source.organizeImports'
  | 'source.fixAll';

export interface CodeAction {
  readonly id: CodeActionId;
  readonly title: string;
  readonly kind: CodeActionKind;
  readonly diagnostics?: DiagnosticInfo[];
  readonly edit?: EditOperation[];
  readonly command?: CommandData;
  readonly isPreferred?: boolean;
}

export interface ParameterInfo {
  readonly label: string | readonly [number, number];
  readonly documentation?: string;
}

export interface SignatureInfo {
  readonly label: string;
  readonly documentation?: string;
  readonly parameters: ParameterInfo[];
}

export interface SignatureHelp {
  readonly signatures: SignatureInfo[];
  readonly activeSignature: number;
  readonly activeParameter: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 NAVIGATION — go-to, rename, call hierarchy, references
// ═══════════════════════════════════════════════════════════════════════════════

export interface LocationLink {
  readonly originSelectionRange?: Range;
  readonly targetUri: string;
  readonly targetRange: Range;
  readonly targetSelectionRange: Range;
}

export interface RenameFileChange {
  readonly uri: string;
  readonly oldText: string;
  readonly newText: string;
  readonly range: Range;
}

export interface RenamePreview {
  readonly changes: RenameFileChange[];
  readonly conflictCount: number;
}

export interface RenameResult {
  readonly changes: Map<string, EditOperation[]>;
  readonly documentChanges?: RenameFileChange[];
}

export type SymbolKind =
  | 'file'
  | 'module'
  | 'namespace'
  | 'package'
  | 'class'
  | 'method'
  | 'property'
  | 'field'
  | 'constructor'
  | 'enum'
  | 'interface'
  | 'function'
  | 'variable'
  | 'constant'
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object'
  | 'key'
  | 'null'
  | 'enum-member'
  | 'struct'
  | 'event'
  | 'operator'
  | 'type-parameter';

export interface CallHierarchyItem {
  readonly name: string;
  readonly kind: SymbolKind;
  readonly uri: string;
  readonly range: Range;
  readonly selectionRange: Range;
  readonly detail?: string;
}

export type CallHierarchyDirection = 'incoming' | 'outgoing';

export interface TypeHierarchyItem {
  readonly name: string;
  readonly kind: SymbolKind;
  readonly uri: string;
  readonly range: Range;
  readonly selectionRange: Range;
  readonly detail?: string;
}

export type TypeHierarchyDirection = 'supertypes' | 'subtypes';

export interface DocumentSymbol {
  readonly name: string;
  readonly detail?: string;
  readonly kind: SymbolKind;
  readonly range: Range;
  readonly selectionRange: Range;
  readonly children?: DocumentSymbol[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §11 PRESENTATION — code lens, outline, inlay hints, formatting, semantic tokens
// ═══════════════════════════════════════════════════════════════════════════════

export interface CommandData {
  readonly id: CommandId;
  readonly title: string;
  readonly category?: string;
  readonly icon?: string;
  readonly precondition?: WhenClause;
  readonly handler?: string;
}

export interface CodeLensData {
  readonly id: CodeLensId;
  readonly range: Range;
  readonly command?: CommandData;
  readonly isResolved: boolean;
}

export type InlayHintKind = 'type' | 'parameter' | 'enum' | 'chaining';

export interface InlayHint {
  readonly position: Position;
  readonly label: string;
  readonly kind: InlayHintKind;
  readonly tooltip?: string;
  readonly paddingLeft?: boolean;
  readonly paddingRight?: boolean;
}

export interface BreadcrumbItem {
  readonly name: string;
  readonly kind: SymbolKind;
  readonly range: Range;
  readonly children?: BreadcrumbItem[];
}

export interface FormatOptions {
  readonly tabSize: number;
  readonly insertSpaces: boolean;
  readonly trimAutoWhitespace?: boolean;
  readonly trimFinalNewlines?: boolean;
  readonly insertFinalNewline?: boolean;
}

export interface SemanticToken {
  readonly line: number;
  readonly startChar: number;
  readonly length: number;
  readonly tokenType: string;
  readonly tokenModifiers: string[];
}

export type SemanticTokenTypes =
  | 'namespace'
  | 'type'
  | 'class'
  | 'enum'
  | 'interface'
  | 'struct'
  | 'typeParameter'
  | 'parameter'
  | 'variable'
  | 'property'
  | 'enumMember'
  | 'event'
  | 'function'
  | 'method'
  | 'macro'
  | 'keyword'
  | 'modifier'
  | 'comment'
  | 'string'
  | 'number'
  | 'regexp'
  | 'operator'
  | 'decorator';

// ═══════════════════════════════════════════════════════════════════════════════
// §12 LAYOUT — viewport, line height, word wrap, scroll, minimap, decorations
// ═══════════════════════════════════════════════════════════════════════════════

export interface ViewportState {
  readonly id: ViewportId;
  readonly startLine: number;
  readonly endLine: number;
  readonly startColumn: number;
  readonly scrollTop: number;
  readonly scrollLeft: number;
  readonly width: number;
  readonly height: number;
  readonly lineHeight: number;
  readonly charWidth: number;
}

export type WordWrapMode = 'off' | 'on' | 'wordWrap' | 'bounded';

export interface WordWrapConfig {
  readonly mode: WordWrapMode;
  readonly column?: number;
}

export interface MinimapConfig {
  readonly enabled: boolean;
  readonly side: 'left' | 'right';
  readonly showSlider: 'always' | 'mouseover';
  readonly renderCharacters: boolean;
  readonly maxColumn: number;
  readonly scale: number;
}

export interface GutterConfig {
  readonly lineNumbers: 'on' | 'off' | 'relative' | 'interval';
  readonly folding: boolean;
  readonly glyphMargin: boolean;
  readonly lineDecorationsWidth: number;
}

export interface ScrollConfig {
  readonly smoothScrolling: boolean;
  readonly scrollBeyondLastLine: boolean;
  readonly scrollBeyondLastColumn: number;
  readonly fastScrollSensitivity: number;
  readonly mouseWheelScrollSensitivity: number;
}

export interface StickyScrollConfig {
  readonly enabled: boolean;
  readonly maxLineCount: number;
  readonly defaultModel: 'outline' | 'folding' | 'indentation';
}

export interface MinimapDecorationOptions {
  readonly color: string;
  readonly position: 'inline' | 'gutter';
}

export interface DecorationOptions {
  readonly className?: string;
  readonly inlineClassName?: string;
  readonly beforeContentClassName?: string;
  readonly afterContentClassName?: string;
  readonly glyphMarginClassName?: string;
  readonly overviewRulerColor?: string;
  readonly minimap?: MinimapDecorationOptions;
  readonly isWholeLine?: boolean;
  readonly stickiness: MarkerStickiness;
  readonly hoverMessage?: string;
}

export interface DecorationData {
  readonly id: DecorationId;
  readonly range: Range;
  readonly options: DecorationOptions;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §13 WIDGETS — zone, content, overlay widgets
// ═══════════════════════════════════════════════════════════════════════════════

export interface ZoneWidget {
  readonly id: WidgetId;
  readonly afterLineNumber: number;
  readonly heightInLines: number;
  readonly domNode?: unknown;
  readonly showInHiddenAreas?: boolean;
}

export type ContentWidgetPositionPreference = 'exact' | 'above' | 'below';

export interface ContentWidgetPosition {
  readonly position: Position;
  readonly preference: ContentWidgetPositionPreference[];
}

export interface ContentWidget {
  readonly id: WidgetId;
  readonly position: ContentWidgetPosition;
  readonly allowEditorOverflow?: boolean;
  readonly suppressMouseDown?: boolean;
}

export type OverlayWidgetPositionPreference =
  | 'top-right-corner'
  | 'bottom-right-corner'
  | 'top-center';

export interface OverlayWidgetPosition {
  readonly preference: OverlayWidgetPositionPreference;
}

export interface OverlayWidget {
  readonly id: WidgetId;
  readonly position: OverlayWidgetPosition;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §14 EDITOR STATE — instance, mode, focus, read-only, diff
// ═══════════════════════════════════════════════════════════════════════════════

export type EditorMode =
  | 'normal'
  | 'insert'
  | 'visual'
  | 'visual-line'
  | 'visual-block'
  | 'command'
  | 'replace'
  | 'select';

export interface FocusState {
  readonly hasFocus: boolean;
  readonly focusTarget: 'editor' | 'widget' | 'input' | 'none';
}

export type ReadOnlyMode = 'off' | 'soft' | 'hard';

export interface EditorConfig {
  readonly tabSize: number;
  readonly insertSpaces: boolean;
  readonly wordWrap: WordWrapConfig;
  readonly minimap: MinimapConfig;
  readonly gutter: GutterConfig;
  readonly scroll: ScrollConfig;
  readonly stickyScroll: StickyScrollConfig;
  readonly formatOptions: FormatOptions;
  readonly cursorStyle: CursorStyle;
  readonly cursorBlinking: 'blink' | 'smooth' | 'phase' | 'expand' | 'solid';
  readonly lineHeight: number;
  readonly fontSize: number;
  readonly fontFamily: string;
  readonly fontWeight: string;
  readonly fontLigatures: boolean;
  readonly renderWhitespace: 'none' | 'boundary' | 'selection' | 'trailing' | 'all';
  readonly renderControlCharacters: boolean;
  readonly renderLineHighlight: 'none' | 'gutter' | 'line' | 'all';
  readonly matchBrackets: 'never' | 'near' | 'always';
  readonly autoClosingBrackets: 'always' | 'languageDefined' | 'beforeWhitespace' | 'never';
  readonly autoClosingQuotes: 'always' | 'languageDefined' | 'beforeWhitespace' | 'never';
  readonly autoSurround: 'languageDefined' | 'quotes' | 'brackets' | 'never';
  readonly autoIndent: 'none' | 'keep' | 'brackets' | 'advanced' | 'full';
  readonly dragAndDrop: boolean;
  readonly emptySelectionClipboard: boolean;
  readonly linkedEditing: boolean;
  readonly occurrencesHighlight: boolean;
  readonly showFoldingControls: 'always' | 'never' | 'mouseover';
  readonly unfoldOnClickAfterEndOfLine: boolean;
  readonly guides: BracketGuideConfig;
}

export interface BracketGuideConfig {
  readonly bracketPairs: boolean | 'active';
  readonly bracketPairsHorizontal: boolean | 'active';
  readonly highlightActiveBracketPair: boolean;
  readonly indentation: boolean;
  readonly highlightActiveIndentation: boolean | 'always';
}

export interface EditorState {
  readonly id: EditorInstanceId;
  readonly documentId: DocumentId;
  readonly cursorGroup: CursorGroup;
  readonly viewport: ViewportState;
  readonly mode: EditorMode;
  readonly focus: FocusState;
  readonly readOnly: ReadOnlyMode;
  readonly config: EditorConfig;
}

export interface CharChange {
  readonly originalStartLineNumber: number;
  readonly originalStartColumn: number;
  readonly originalEndLineNumber: number;
  readonly originalEndColumn: number;
  readonly modifiedStartLineNumber: number;
  readonly modifiedStartColumn: number;
  readonly modifiedEndLineNumber: number;
  readonly modifiedEndColumn: number;
}

export interface DiffChange {
  readonly originalStartLineNumber: number;
  readonly originalEndLineNumber: number;
  readonly modifiedStartLineNumber: number;
  readonly modifiedEndLineNumber: number;
  readonly charChanges?: CharChange[];
}

export interface DiffEditorState {
  readonly originalId: DocumentId;
  readonly modifiedId: DocumentId;
  readonly changes: DiffChange[];
  readonly renderSideBySide: boolean;
  readonly ignoreTrimWhitespace: boolean;
  readonly maxComputationTime: number;
}

export interface LinkedEditorState {
  readonly editorIds: EditorInstanceId[];
  readonly syncScrolling: boolean;
  readonly syncCursors: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §15 FIND & REPLACE
// ═══════════════════════════════════════════════════════════════════════════════

export interface FindState {
  readonly searchString: string;
  readonly replaceString: string;
  readonly isRegex: boolean;
  readonly isCaseSensitive: boolean;
  readonly isWholeWord: boolean;
  readonly searchScope: Range | null;
  readonly matchCount: number;
  readonly currentMatch: number;
  readonly preserveCase: boolean;
}

export interface FindMatch {
  readonly range: Range;
  readonly matches: string[];
}

export interface FileSearchResult {
  readonly uri: string;
  readonly matches: FindMatch[];
  readonly lineCount: number;
}

export interface FindInFilesState {
  readonly query: string;
  readonly include: string;
  readonly exclude: string;
  readonly isRegex: boolean;
  readonly isCaseSensitive: boolean;
  readonly isWholeWord: boolean;
  readonly results: FileSearchResult[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §16 SNIPPETS
// ═══════════════════════════════════════════════════════════════════════════════

export interface SnippetTransform {
  readonly regex: string;
  readonly replacement: string;
  readonly options?: string;
}

export interface TabStop {
  readonly index: number;
  readonly range: Range;
  readonly placeholder?: string;
  readonly choices?: string[];
  readonly transform?: SnippetTransform;
}

export interface SnippetDefinition {
  readonly id: SnippetId;
  readonly name: string;
  readonly prefix: string[];
  readonly body: string[];
  readonly description?: string;
  readonly scope?: LanguageId[];
  readonly isBuiltin: boolean;
}

export interface SnippetSession {
  readonly snippetId: SnippetId;
  readonly tabStops: TabStop[];
  readonly currentTabStop: number;
  readonly placeholders: Map<number, string>;
}

export type SnippetVariable =
  | 'TM_SELECTED_TEXT'
  | 'TM_CURRENT_LINE'
  | 'TM_CURRENT_WORD'
  | 'TM_LINE_INDEX'
  | 'TM_LINE_NUMBER'
  | 'TM_FILENAME'
  | 'TM_FILENAME_BASE'
  | 'TM_DIRECTORY'
  | 'TM_FILEPATH'
  | 'CLIPBOARD'
  | 'WORKSPACE_NAME'
  | 'WORKSPACE_FOLDER'
  | 'CURSOR_INDEX'
  | 'CURSOR_NUMBER'
  | 'CURRENT_YEAR'
  | 'CURRENT_MONTH'
  | 'CURRENT_DATE'
  | 'CURRENT_HOUR'
  | 'CURRENT_MINUTE'
  | 'CURRENT_SECOND'
  | 'RANDOM'
  | 'RANDOM_HEX'
  | 'UUID'
  | 'BLOCK_COMMENT_START'
  | 'BLOCK_COMMENT_END'
  | 'LINE_COMMENT';

// ═══════════════════════════════════════════════════════════════════════════════
// §17 COMMANDS & KEYBINDINGS
// ═══════════════════════════════════════════════════════════════════════════════

export type WhenClause = string;

export interface CommandRegistry {
  readonly commands: Map<CommandId, CommandData>;
}

export interface KeybindingData {
  readonly id: KeybindingId;
  readonly key: string;
  readonly command: CommandId;
  readonly when?: WhenClause;
  readonly args?: unknown;
}

export interface ChordKeybinding {
  readonly firstPart: string;
  readonly chordPart: string;
}

export interface KeybindingResolveResult {
  readonly command: CommandId;
  readonly args?: unknown;
  readonly when?: WhenClause;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §17b KEYBINDINGS STATE — 3.0.0 additions
// ═══════════════════════════════════════════════════════════════════════════════

export interface KeyBinding {
  readonly id: KeybindingId;
  readonly key: string;
  readonly command: CommandId;
  readonly when?: WhenClause;
  readonly args?: unknown;
  readonly isChord: boolean;
  readonly chordFirstPart?: string;
  readonly chordSecondPart?: string;
  readonly source: 'default' | 'user' | 'extension';
  readonly weight: number;
}

export interface KeyContext {
  readonly id: KeyContextId;
  readonly key: string;
  readonly value: boolean | string | number;
}

export interface KeybindingsState {
  readonly bindings: ReadonlyArray<KeyBinding>;
  readonly pendingChordKey: string | undefined;
  readonly pendingChordId: ChordId | undefined;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §17c THEMES STATE — 3.0.0 additions
// ═══════════════════════════════════════════════════════════════════════════════

export type ThemeType = 'dark' | 'light' | 'high-contrast-dark' | 'high-contrast-light';

export interface TokenColor {
  readonly scope: string | ReadonlyArray<string>;
  readonly settings: {
    readonly foreground?: string;
    readonly background?: string;
    readonly fontStyle?: string;
  };
}

export interface ThemeDefinition {
  readonly id: ThemeId;
  readonly name: string;
  readonly type: ThemeType;
  readonly colors: Readonly<Record<string, string>>;
  readonly tokenColors: ReadonlyArray<TokenColor>;
}

export interface ThemesState {
  readonly themes: ReadonlyArray<ThemeDefinition>;
  readonly activeThemeId: ThemeId | undefined;
  readonly cssVariables: Readonly<Record<string, string>>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §17d SEARCH STATE — 3.0.0 additions
// ═══════════════════════════════════════════════════════════════════════════════

export type SearchScope = 'workspace' | 'openFiles' | 'folder' | 'custom';

export type SearchSessionStatus = 'idle' | 'running' | 'complete' | 'cancelled' | 'error';

export interface SearchSession {
  readonly id: SearchSessionId;
  readonly query: SearchQuery;
  readonly results: ReadonlyArray<SearchResult>;
  readonly status: SearchSessionStatus;
  readonly startedAt: number;
  readonly completedAt: number | undefined;
  readonly totalMatchCount: number;
}

export interface SearchState {
  readonly sessions: ReadonlyArray<SearchSession>;
  readonly activeSessionId: SearchSessionId | undefined;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §18 ACCESSIBILITY
// ═══════════════════════════════════════════════════════════════════════════════

export interface AccessibilityConfig {
  readonly screenReaderEnabled: boolean;
  readonly tabFocusMode: boolean;
  readonly accessibilitySupport: 'auto' | 'on' | 'off';
  readonly accessibilityPageSize: number;
  readonly highContrast: boolean;
  readonly reducedMotion: boolean;
  readonly ariaLabel: string;
}

export interface AccessibilityInfo {
  readonly linesCount: number;
  readonly lineNumber: number;
  readonly columnNumber: number;
  readonly selectedText?: string;
  readonly suggestion?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §19 TERMINAL — entity, status, type, session, profile
// ═══════════════════════════════════════════════════════════════════════════════

export type TerminalType =
  | 'local'
  | 'ssh'
  | 'docker'
  | 'kubernetes'
  | 'cloud'
  | 'multiplexed'
  | 'shared'
  | 'recorded'
  | 'sandbox';

export type TerminalStatus =
  | 'initializing'
  | 'connecting'
  | 'active'
  | 'busy'
  | 'disconnected'
  | 'error'
  | 'closed'
  | 'suspended';

export type ShellType =
  | 'bash'
  | 'zsh'
  | 'fish'
  | 'powershell'
  | 'pwsh'
  | 'cmd'
  | 'sh'
  | 'dash'
  | 'ksh'
  | 'csh'
  | 'tcsh'
  | 'nu'
  | 'elvish'
  | 'xonsh'
  | 'ion'
  | 'oil'
  | 'murex'
  | 'hilbish'
  | 'nushell'
  | 'custom';

export interface TerminalTheme {
  readonly background: string;
  readonly foreground: string;
  readonly cursor: string;
  readonly cursorAccent: string;
  readonly selectionBackground: string;
  readonly selectionForeground: string;
  readonly ansiColors: readonly [string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string];
}

export interface TerminalRenderingConfig {
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fontWeight: string;
  readonly lineHeight: number;
  readonly letterSpacing: number;
  readonly cursorStyle: CursorStyle;
  readonly cursorBlink: boolean;
  readonly cursorWidth: number;
  readonly scrollback: number;
  readonly theme: TerminalTheme;
  readonly minimumContrastRatio: number;
}

export interface TerminalBehaviorConfig {
  readonly autoClose: boolean;
  readonly confirmOnExit: boolean;
  readonly clearOnStart: boolean;
  readonly copyOnSelection: boolean;
  readonly rightClickBehavior: 'default' | 'copyPaste' | 'paste' | 'selectWord';
  readonly scrollToBottom: boolean;
  readonly detectUrls: boolean;
  readonly enableBell: boolean;
  readonly wordSeparators: string;
}

export interface TerminalProfile {
  readonly name: string;
  readonly shell: ShellType;
  readonly args: string[];
  readonly env: Record<string, string>;
  readonly cwd: string;
  readonly rendering: TerminalRenderingConfig;
  readonly behavior: TerminalBehaviorConfig;
  readonly keybindings: Record<string, string>;
}

export interface TerminalSecurityContext {
  readonly userId: string;
  readonly permissions: string[];
  readonly commandRiskLevel: 'safe' | 'moderate' | 'dangerous' | 'critical';
}

export interface TerminalMetrics {
  readonly cpuUsage: number;
  readonly memoryUsage: number;
  readonly bytesRead: number;
  readonly bytesWritten: number;
  readonly throughput: number;
}

export interface TerminalState {
  readonly id: TerminalId;
  readonly name: string;
  readonly type: TerminalType;
  readonly status: TerminalStatus;
  readonly session: TerminalSessionState;
  readonly buffer: TerminalBufferState;
  readonly pty: PTYState;
  readonly profile: TerminalProfile;
  readonly readOnly?: boolean;
  readonly aiAssistance?: TerminalAIState;
  readonly cloudProvider?: CloudTerminalState;
  readonly collaboration?: TerminalCollabState;
  readonly recording?: RecordingState;
  readonly securityContext: TerminalSecurityContext;
  readonly metrics: TerminalMetrics;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §20 PTY — process, configuration, platform
// ═══════════════════════════════════════════════════════════════════════════════

export type PTYStatus =
  | 'starting'
  | 'running'
  | 'suspended'
  | 'exited'
  | 'killed'
  | 'error';

export type PTYSignal =
  | 'SIGTERM'
  | 'SIGKILL'
  | 'SIGINT'
  | 'SIGSTOP'
  | 'SIGCONT'
  | 'SIGHUP'
  | 'SIGUSR1'
  | 'SIGUSR2';

export type PTYPlatform = 'darwin' | 'linux' | 'win32' | 'freebsd' | 'openbsd';

export interface PTYResourceUsage {
  readonly cpuPercent: number;
  readonly memoryBytes: number;
  readonly openFiles: number;
}

export interface PTYState {
  readonly processId: PTYProcessId;
  readonly pid: number;
  readonly shell: ShellType;
  readonly cwd: string;
  readonly cols: number;
  readonly rows: number;
  readonly status: PTYStatus;
  readonly resourceUsage: PTYResourceUsage;
}

export interface PTYConfig {
  readonly shell: ShellType;
  readonly args: string[];
  readonly env: Record<string, string>;
  readonly cwd: string;
  readonly cols: number;
  readonly rows: number;
  readonly encoding: string;
  readonly handleFlowControl: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §21 TERMINAL BUFFER — cells, colors, cursor, scrollback
// ═══════════════════════════════════════════════════════════════════════════════

export type ColorMode =
  | 'default'
  | '3bit'
  | '4bit'
  | '8bit'
  | '24bit'
  | 'indexed'
  | 'theme';

export interface TerminalColor {
  readonly mode: ColorMode;
  readonly r?: number;
  readonly g?: number;
  readonly b?: number;
  readonly index?: number;
  readonly theme?: string;
}

/**
 * CellFlags is a bitmask:
 * bold=1, italic=2, underline=4, strikethrough=8, blink=16,
 * inverse=32, invisible=64, dim=128, overline=256,
 * double-underline=512, curly-underline=1024
 */
export type CellFlags = number;

export interface CellAttributes {
  readonly href?: string;
  readonly tooltip?: string;
  readonly syntaxScope?: string;
  readonly accessibilityLabel?: string;
}

export interface TerminalCell {
  readonly char: string;
  readonly width: number;
  readonly fg: TerminalColor;
  readonly bg: TerminalColor;
  readonly flags: CellFlags;
  readonly attrs?: CellAttributes;
}

export interface TerminalLine {
  readonly cells: TerminalCell[];
  readonly isWrapped: boolean;
  readonly version: number;
}

export interface TerminalCursorState {
  readonly row: number;
  readonly col: number;
  readonly style: CursorStyle;
  readonly visible: boolean;
  readonly blink: boolean;
}

export interface ScrollbackConfig {
  readonly maxLines: number;
  readonly reflow: boolean;
}

export interface TerminalViewport {
  readonly startLine: number;
  readonly endLine: number;
  readonly scrollTop: number;
  readonly height: number;
}

export interface TerminalBufferState {
  readonly id: TerminalBufferId;
  readonly lines: TerminalLine[];
  readonly altLines: TerminalLine[];
  readonly activeBuffer: 'normal' | 'alternate';
  readonly cursorState: TerminalCursorState;
  readonly scrollback: ScrollbackConfig;
  readonly viewport: TerminalViewport;
  readonly dirtyRegions: Range[];
  readonly modificationSeq: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §22 ANSI — sequence types, SGR, modes
// ═══════════════════════════════════════════════════════════════════════════════

export type ANSISequenceType =
  | 'csi'
  | 'osc'
  | 'dcs'
  | 'apc'
  | 'pm'
  | 'sos'
  | 'c0'
  | 'c1'
  | 'sgr'
  | 'cursor'
  | 'erase'
  | 'scroll'
  | 'mode'
  | 'charset'
  | 'hyperlink'
  | 'bell'
  | 'title';

export interface ANSISequence {
  readonly type: ANSISequenceType;
  readonly raw: string;
  readonly command?: string;
  readonly params: number[];
  readonly stringParam?: string;
}

export type SGRAttributeType =
  | 'reset'
  | 'bold'
  | 'dim'
  | 'italic'
  | 'underline'
  | 'slow-blink'
  | 'rapid-blink'
  | 'inverse'
  | 'invisible'
  | 'strikethrough'
  | 'double-underline'
  | 'normal-intensity'
  | 'not-italic'
  | 'not-underline'
  | 'not-blink'
  | 'not-inverse'
  | 'not-invisible'
  | 'not-strikethrough'
  | 'foreground'
  | 'background'
  | 'underline-color'
  | 'overline'
  | 'not-overline'
  | 'subscript'
  | 'superscript';

export interface SGRAttribute {
  readonly attribute: SGRAttributeType;
  readonly value?: number | TerminalColor;
}

export type MouseTrackingMode =
  | 'none'
  | 'x10'
  | 'normal'
  | 'button'
  | 'any'
  | 'sgr'
  | 'urxvt';

export interface TerminalModeState {
  readonly applicationCursor: boolean;
  readonly autoWrap: boolean;
  readonly bracketedPaste: boolean;
  readonly insertMode: boolean;
  readonly originMode: boolean;
  readonly reverseVideo: boolean;
  readonly mouseTracking: MouseTrackingMode;
  readonly focusTracking: boolean;
  readonly alternateScreen: boolean;
}

export type CharsetDesignation =
  | 'ascii'
  | 'dec-special'
  | 'uk'
  | 'us'
  | 'german'
  | 'french'
  | 'italian'
  | 'spanish'
  | 'swedish';

// ═══════════════════════════════════════════════════════════════════════════════
// §23 TERMINAL OUTPUT — processing, format detection, entity extraction
// ═══════════════════════════════════════════════════════════════════════════════

export type OutputClassification =
  | 'command-output'
  | 'error-message'
  | 'warning'
  | 'success'
  | 'status'
  | 'data'
  | 'log'
  | 'prompt'
  | 'system'
  | 'interactive'
  | 'progress';

export type EntityType =
  | 'file-path'
  | 'url'
  | 'email'
  | 'ip-address'
  | 'port'
  | 'pid'
  | 'timestamp'
  | 'error-code'
  | 'version'
  | 'git-hash'
  | 'uuid'
  | 'env-var'
  | 'docker-id'
  | 'hostname';

export type DetectedFormat =
  | 'json'
  | 'xml'
  | 'csv'
  | 'tsv'
  | 'table'
  | 'log'
  | 'key-value'
  | 'list'
  | 'tree'
  | 'yaml'
  | 'toml'
  | 'ini'
  | 'plain';

export interface ExtractedEntity {
  readonly type: EntityType;
  readonly value: string;
  readonly range: Range;
  readonly confidence: number;
}

export interface OutputContent {
  readonly text: string;
  readonly structured?: unknown;
}

export interface ProcessedOutput {
  readonly raw: string;
  readonly content: OutputContent;
  readonly classification: OutputClassification;
  readonly entities: ExtractedEntity[];
  readonly format?: DetectedFormat;
  readonly timestamp: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §24 TERMINAL SESSION — shell, history, jobs, prompt
// ═══════════════════════════════════════════════════════════════════════════════

export type SessionStatus =
  | 'starting'
  | 'active'
  | 'busy'
  | 'suspended'
  | 'terminated'
  | 'error';

export interface CommandHistoryEntry {
  readonly command: string;
  readonly timestamp: number;
  readonly exitCode?: number;
  readonly duration?: number;
  readonly cwd: string;
}

export interface CommandHistoryState {
  readonly entries: CommandHistoryEntry[];
  readonly maxSize: number;
  readonly searchIndex?: number;
}

export interface JobInfo {
  readonly id: number;
  readonly command: string;
  readonly status: 'running' | 'stopped' | 'done' | 'killed';
  readonly pid: number;
  readonly isForeground: boolean;
}

export interface PromptConfig {
  readonly template: string;
  readonly showGitBranch: boolean;
  readonly showGitStatus: boolean;
  readonly showCwd: boolean;
  readonly showUser: boolean;
  readonly showHost: boolean;
  readonly colors: Record<string, string>;
}

export interface TerminalSessionState {
  readonly id: TerminalSessionId;
  readonly shell: ShellType;
  readonly status: SessionStatus;
  readonly env: Record<string, string>;
  readonly cwd: string;
  readonly history: CommandHistoryState;
  readonly jobs: JobInfo[];
  readonly promptConfig: PromptConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §25 MULTIPLEXER — sessions, windows, panes, layouts
// ═══════════════════════════════════════════════════════════════════════════════

export type MultiplexerType = 'tmux' | 'screen' | 'built-in';

export type PaneLayout =
  | 'tiled'
  | 'even-horizontal'
  | 'even-vertical'
  | 'main-horizontal'
  | 'main-vertical'
  | 'custom';

export type PaneSplitDirection = 'horizontal' | 'vertical';

export interface PanePosition {
  readonly row: number;
  readonly col: number;
}

export interface PaneSize {
  readonly width: number;
  readonly height: number;
  readonly minWidth?: number;
  readonly minHeight?: number;
}

export interface MultiplexPane {
  readonly id: MultiplexPaneId;
  readonly terminalId: TerminalId;
  readonly position: PanePosition;
  readonly size: PaneSize;
  readonly isActive: boolean;
  readonly isBorderVisible: boolean;
  readonly title?: string;
}

export interface MultiplexWindow {
  readonly id: MultiplexWindowId;
  readonly name: string;
  readonly panes: MultiplexPane[];
  readonly activePaneId: MultiplexPaneId;
  readonly layout: PaneLayout;
}

export interface MultiplexSession {
  readonly id: MultiplexSessionId;
  readonly name: string;
  readonly windows: MultiplexWindow[];
  readonly activeWindowIndex: number;
  readonly isAttached: boolean;
  readonly createdAt: number;
}

export interface StatusBarConfig {
  readonly enabled: boolean;
  readonly position: 'top' | 'bottom';
  readonly leftContent: string;
  readonly rightContent: string;
  readonly style: string;
}

export interface MultiplexerConfig {
  readonly prefix: string;
  readonly mouse: boolean;
  readonly statusBar: StatusBarConfig;
  readonly defaultShell?: ShellType;
}

export interface MultiplexerState {
  readonly type: MultiplexerType;
  readonly sessions: MultiplexSession[];
  readonly activeSessionId?: MultiplexSessionId;
  readonly config: MultiplexerConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §26 RECORDING — frames, metadata, playback, export
// ═══════════════════════════════════════════════════════════════════════════════

export type RecordingStatus =
  | 'idle'
  | 'recording'
  | 'paused'
  | 'stopped'
  | 'exporting';

export type FrameType =
  | 'data'
  | 'marker'
  | 'command'
  | 'output'
  | 'error'
  | 'resize'
  | 'metadata';

export interface RecordingFrame {
  readonly id: RecordingFrameId;
  readonly type: FrameType;
  readonly data: string;
  readonly timestamp: number;
  readonly duration?: number;
}

export interface RecordingMetadata {
  readonly title?: string;
  readonly description?: string;
  readonly duration: number;
  readonly frameCount: number;
  readonly commandCount: number;
  readonly startedAt: number;
  readonly endedAt?: number;
  readonly tags: string[];
  readonly author?: string;
}

export interface RecordingConfig {
  readonly maxDuration?: number;
  readonly maxFrames?: number;
  readonly compression: boolean;
  readonly autoSave: boolean;
  readonly captureKeystrokes: boolean;
}

export interface RecordingBookmark {
  readonly id: string;
  readonly frameIndex: number;
  readonly label: string;
  readonly timestamp: number;
}

export type RecordingExportFormat =
  | 'asciinema'
  | 'webm'
  | 'mp4'
  | 'gif'
  | 'text-log'
  | 'vtt'
  | 'zip';

export interface PlaybackState {
  readonly recording: RecordingId;
  readonly currentFrame: number;
  readonly speed: number;
  readonly isPlaying: boolean;
  readonly isPaused: boolean;
  readonly startTime: number;
}

export interface RecordingState {
  readonly id: RecordingId;
  readonly terminalId: TerminalId;
  readonly status: RecordingStatus;
  readonly frames: RecordingFrame[];
  readonly metadata: RecordingMetadata;
  readonly config: RecordingConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §27 TERMINAL COLLABORATION — sharing, participants, permissions
// ═══════════════════════════════════════════════════════════════════════════════

export type ParticipantRole = 'owner' | 'editor' | 'viewer' | 'commenter';

export interface ShareParticipant {
  readonly id: ParticipantId;
  readonly name: string;
  readonly role: ParticipantRole;
  readonly status: 'connected' | 'disconnected' | 'idle';
  readonly cursor?: TerminalCursorState;
  readonly joinedAt: number;
}

export interface SharePermissions {
  readonly read: boolean;
  readonly write: boolean;
  readonly execute: boolean;
  readonly share: boolean;
  readonly manage: boolean;
}

export interface ChatMessage {
  readonly id: string;
  readonly authorId: ParticipantId;
  readonly text: string;
  readonly timestamp: number;
}

export interface ShareConfig {
  readonly maxParticipants: number;
  readonly requireAuth: boolean;
  readonly expiresAt?: number;
  readonly allowRecording: boolean;
  readonly encryptionEnabled: boolean;
}

export interface TerminalCollabState {
  readonly shareSessionId: ShareSessionId;
  readonly isHost: boolean;
  readonly participants: ShareParticipant[];
  readonly permissions: SharePermissions;
  readonly chat: ChatMessage[];
  readonly config: ShareConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §28 CLOUD TERMINAL — providers, containers, scaling
// ═══════════════════════════════════════════════════════════════════════════════

export type CloudProvider =
  | 'aws'
  | 'gcp'
  | 'azure'
  | 'digital-ocean'
  | 'kubernetes'
  | 'local-docker';

export interface CloudConnection {
  readonly endpoint: string;
  readonly protocol: 'ssh' | 'websocket' | 'http' | 'grpc';
  readonly status: 'connected' | 'disconnected' | 'reconnecting';
  readonly latency: number;
  readonly encrypted: boolean;
}

export interface PortMapping {
  readonly containerPort: number;
  readonly hostPort: number;
  readonly protocol: 'tcp' | 'udp';
}

export interface VolumeMount {
  readonly source: string;
  readonly target: string;
  readonly readOnly: boolean;
}

export interface ContainerInfo {
  readonly id: string;
  readonly image: string;
  readonly status: 'creating' | 'running' | 'paused' | 'stopped' | 'removing';
  readonly ports: PortMapping[];
  readonly volumes: VolumeMount[];
  readonly env: Record<string, string>;
}

export interface ResourceLimits {
  readonly cpuCores: number;
  readonly memoryMb: number;
  readonly storageMb: number;
  readonly networkMbps?: number;
}

export interface ScalingConfig {
  readonly enabled: boolean;
  readonly minReplicas: number;
  readonly maxReplicas: number;
  readonly cpuThreshold: number;
  readonly memoryThreshold: number;
}

export interface HealthCheckConfig {
  readonly command: string;
  readonly interval: number;
  readonly timeout: number;
  readonly retries: number;
  readonly startPeriod: number;
}

export interface CloudTerminalState {
  readonly id: CloudTerminalId;
  readonly provider: CloudProvider;
  readonly connection: CloudConnection;
  readonly container?: ContainerInfo;
  readonly resourceLimits: ResourceLimits;
  readonly scaling?: ScalingConfig;
  readonly healthCheck?: HealthCheckConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §29 TERMINAL AI — suggestions, error analysis, NL interface
// ═══════════════════════════════════════════════════════════════════════════════

export interface CommandSuggestion {
  readonly command: string;
  readonly description: string;
  readonly confidence: number;
  readonly source: 'history' | 'context' | 'ai' | 'pattern';
  readonly risk: 'safe' | 'moderate' | 'dangerous';
  readonly isPersonalized: boolean;
}

export interface SuggestedSolution {
  readonly command: string;
  readonly description: string;
  readonly confidence: number;
  readonly risk: 'safe' | 'moderate' | 'dangerous';
}

export interface ErrorAnalysis {
  readonly error: string;
  readonly explanation: string;
  readonly solutions: SuggestedSolution[];
  readonly commonCause: string;
  readonly relatedCommands: string[];
}

export interface NLContext {
  readonly cwd: string;
  readonly recentCommands: string[];
  readonly shellType: ShellType;
  readonly os: PTYPlatform;
}

export interface NLCommandRequest {
  readonly query: string;
  readonly context: NLContext;
  readonly maxResults: number;
}

export interface NLCommandResult {
  readonly command: string;
  readonly explanation: string;
  readonly confidence: number;
  readonly alternatives: string[];
}

export interface AIPrivacySettings {
  readonly storeHistory: boolean;
  readonly shareData: boolean;
  readonly anonymizeData: boolean;
  readonly retentionDays: number;
}

export interface TerminalAIState {
  readonly enabled: boolean;
  readonly suggestions: CommandSuggestion[];
  readonly recentAnalyses: ErrorAnalysis[];
  readonly privacySettings: AIPrivacySettings;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §30 AI REQUEST/RESPONSE — envelope, routing, confidence
// ═══════════════════════════════════════════════════════════════════════════════

export type AIModule =
  | 'editor'
  | 'file'
  | 'search'
  | 'terminal'
  | 'collaboration'
  | 'project'
  | 'workspace'
  | 'git'
  | 'review';

export type AIRequestType =
  | 'completion'
  | 'suggestion'
  | 'analysis'
  | 'generation'
  | 'conversation'
  | 'refactor'
  | 'explain'
  | 'fix'
  | 'optimize'
  | 'translate'
  | 'document'
  | 'test-generate';

export type AIPriority = 'low' | 'normal' | 'high' | 'critical';

export interface AIContext {
  readonly workspaceId?: WorkspaceId;
  readonly fileUri?: string;
  readonly language?: LanguageId;
  readonly cursorPosition?: Position;
  readonly selection?: Selection;
  readonly symbols?: string[];
  readonly imports?: string[];
}

export interface AIConstraints {
  readonly maxTokens?: number;
  readonly maxLatencyMs?: number;
  readonly maxCost?: number;
  readonly requiredCapabilities?: string[];
}

export interface AIRequest {
  readonly id: AIRequestId;
  readonly module: AIModule;
  readonly type: AIRequestType;
  readonly context: AIContext;
  readonly constraints?: AIConstraints;
  readonly priority: AIPriority;
  readonly qualityThreshold: number;
  readonly timestamp: number;
}

export type AIResponseStatus =
  | 'success'
  | 'partial'
  | 'error'
  | 'timeout'
  | 'cancelled';

export interface AIContent {
  readonly type: 'text' | 'code' | 'structured' | 'suggestions' | 'analysis' | 'conversation';
  readonly value: unknown;
}

export interface AIModelInfo {
  readonly id: AIModelId;
  readonly provider: string;
  readonly name: string;
  readonly version: string;
}

export interface AIPerformanceInfo {
  readonly totalTimeMs: number;
  readonly inferenceTimeMs: number;
  readonly tokensProcessed: number;
  readonly tokensGenerated: number;
  readonly cached: boolean;
}

export interface AIResponse {
  readonly id: AIResponseId;
  readonly requestId: AIRequestId;
  readonly status: AIResponseStatus;
  readonly content: AIContent;
  readonly model: AIModelInfo;
  readonly performance: AIPerformanceInfo;
  readonly confidence: number;
  readonly metadata?: Record<string, unknown>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §31 AI AGENTS — agent entity, tasks, memory, execution plans
// ═══════════════════════════════════════════════════════════════════════════════

export type AIAgentType =
  | 'code-generator'
  | 'code-reviewer'
  | 'test-generator'
  | 'refactoring-expert'
  | 'debugging-specialist'
  | 'security-auditor'
  | 'performance-optimizer'
  | 'project-manager'
  | 'architect'
  | 'devops-engineer'
  | 'qa-engineer'
  | 'documentation-writer';

export type AIAgentStatus =
  | 'idle'
  | 'planning'
  | 'executing'
  | 'waiting'
  | 'learning'
  | 'error'
  | 'paused'
  | 'terminated';

export type AITaskType =
  | 'code-generation'
  | 'code-review'
  | 'testing'
  | 'documentation'
  | 'refactoring'
  | 'debugging'
  | 'optimization'
  | 'analysis'
  | 'planning'
  | 'deployment';

export interface AITaskResult {
  readonly success: boolean;
  readonly output: unknown;
  readonly artifacts: string[];
  readonly duration: number;
  readonly tokensUsed: number;
}

export interface AIAgentTask {
  readonly id: string;
  readonly type: AITaskType;
  readonly parameters: Record<string, unknown>;
  readonly constraints: AIConstraints;
  readonly priority: AIPriority;
  readonly deadline?: number;
  readonly dependencies: string[];
  readonly progress: number;
  readonly result?: AITaskResult;
}

export interface AIEpisode {
  readonly id: string;
  readonly type: string;
  readonly context: string;
  readonly outcome: string;
  readonly timestamp: number;
  readonly relevance: number;
}

export interface AIAgentMemory {
  readonly shortTerm: Record<string, unknown>;
  readonly longTerm: Record<string, unknown>;
  readonly episodic: AIEpisode[];
  readonly working: unknown[];
}

export interface AIExecutionStep {
  readonly id: string;
  readonly type: 'analysis' | 'generation' | 'validation' | 'execution' | 'communication' | 'learning' | 'decision' | 'wait';
  readonly description: string;
  readonly dependencies: string[];
  readonly estimatedDuration: number;
  readonly status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
}

export interface AIExecutionPlan {
  readonly steps: AIExecutionStep[];
  readonly estimatedDuration: number;
  readonly risks: string[];
  readonly successCriteria: string[];
}

export interface AIAgentPerformance {
  readonly tasksCompleted: number;
  readonly tasksSucceeded: number;
  readonly averageDuration: number;
  readonly averageQuality: number;
}

export interface AIAgentConfig {
  readonly maxConcurrentTasks: number;
  readonly learningRate: number;
  readonly autonomyLevel: 'supervised' | 'semi-autonomous' | 'autonomous';
  readonly modelPreference?: AIModelId;
}

export interface AIAgentState {
  readonly id: AIAgentId;
  readonly name: string;
  readonly type: AIAgentType;
  readonly status: AIAgentStatus;
  readonly capabilities: string[];
  readonly memory: AIAgentMemory;
  readonly currentTask?: AIAgentTask;
  readonly performance: AIAgentPerformance;
  readonly config: AIAgentConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §32 AI CONVERSATION — messages, dialogue, intent
// ═══════════════════════════════════════════════════════════════════════════════

export type ConversationType =
  | 'chat'
  | 'assistant'
  | 'collaborative'
  | 'instructional'
  | 'debugging'
  | 'code-review'
  | 'planning'
  | 'support';

export interface ConversationParticipant {
  readonly id: string;
  readonly name: string;
  readonly role: 'user' | 'assistant' | 'system' | 'agent' | 'tool';
  readonly isActive: boolean;
}

export interface MessageContent {
  readonly text?: string;
  readonly code?: { readonly language: string; readonly value: string };
  readonly structured?: unknown;
}

export type MessageType =
  | 'text'
  | 'code'
  | 'image'
  | 'file'
  | 'system'
  | 'action'
  | 'error'
  | 'suggestion';

export interface MessageReaction {
  readonly emoji: string;
  readonly userId: string;
  readonly timestamp: number;
}

export interface MessageAttachment {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly size: number;
  readonly uri: string;
}

export interface AIMessage {
  readonly id: AIMessageId;
  readonly authorId: string;
  readonly role: 'user' | 'assistant' | 'system' | 'agent' | 'tool';
  readonly content: MessageContent;
  readonly type: MessageType;
  readonly timestamp: number;
  readonly parentId?: AIMessageId;
  readonly reactions: MessageReaction[];
  readonly attachments: MessageAttachment[];
}

export interface ConversationContext {
  readonly workspaceId?: WorkspaceId;
  readonly activeFile?: string;
  readonly selectedCode?: string;
  readonly diagnostics?: DiagnosticInfo[];
  readonly recentCommands?: string[];
}

export interface AIConversationState {
  readonly id: AIConversationId;
  readonly type: ConversationType;
  readonly participants: ConversationParticipant[];
  readonly messages: AIMessage[];
  readonly context: ConversationContext;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface IntentResult {
  readonly intent: string;
  readonly confidence: number;
  readonly entities: Record<string, string>;
  readonly suggestedAction?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §33 AI CODE GENERATION — request, response, quality, security
// ═══════════════════════════════════════════════════════════════════════════════

export type CodeGenerationType =
  | 'completion'
  | 'function'
  | 'class'
  | 'interface'
  | 'component'
  | 'test'
  | 'documentation'
  | 'refactor'
  | 'fix'
  | 'optimize'
  | 'translate'
  | 'scaffold'
  | 'custom';

export interface CodeGenerationContext {
  readonly fileUri: string;
  readonly language: LanguageId;
  readonly cursorPosition: Position;
  readonly selectedText?: string;
  readonly surroundingCode: string;
  readonly imports: string[];
  readonly symbols: string[];
  readonly projectType?: string;
}

export interface CodeGenerationConstraints {
  readonly maxLength?: number;
  readonly minLength?: number;
  readonly patterns?: string[];
  readonly dependencies?: string[];
  readonly performanceTarget?: string;
  readonly securityLevel?: string;
}

export interface CodeGenerationPreferences {
  readonly codeStyle: 'concise' | 'verbose' | 'balanced';
  readonly errorHandling: 'try-catch' | 'result-type' | 'optional' | 'assertion';
  readonly testingApproach: 'unit' | 'integration' | 'e2e' | 'property-based';
  readonly documentationLevel: 'none' | 'minimal' | 'standard' | 'comprehensive';
}

export interface CodeGenerationRequest {
  readonly type: CodeGenerationType;
  readonly context: CodeGenerationContext;
  readonly constraints: CodeGenerationConstraints;
  readonly preferences: CodeGenerationPreferences;
}

export interface CodeExplanation {
  readonly summary: string;
  readonly steps: string[];
  readonly designDecisions: string[];
  readonly assumptions: string[];
}

export interface QualityAssessment {
  readonly score: number;
  readonly readability: number;
  readonly maintainability: number;
  readonly testability: number;
  readonly issues: string[];
}

export interface SecurityVulnerability {
  readonly type: string;
  readonly severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  readonly description: string;
  readonly location?: Range;
  readonly mitigation: string;
  readonly cweId?: string;
}

export interface SecurityAssessment {
  readonly score: number;
  readonly vulnerabilities: SecurityVulnerability[];
  readonly recommendations: string[];
}

export interface AlternativeCode {
  readonly code: string;
  readonly description: string;
  readonly tradeoffs: string[];
}

export interface CodeGenerationResult {
  readonly code: string;
  readonly language: string;
  readonly explanation: CodeExplanation;
  readonly quality: QualityAssessment;
  readonly security: SecurityAssessment;
  readonly alternatives: AlternativeCode[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §34 AI ANALYSIS — types, metrics, complexity
// ═══════════════════════════════════════════════════════════════════════════════

export type CodeAnalysisType =
  | 'quality'
  | 'performance'
  | 'security'
  | 'maintainability'
  | 'architecture'
  | 'dependencies'
  | 'testing'
  | 'documentation'
  | 'complexity'
  | 'duplication'
  | 'smell'
  | 'bug'
  | 'style'
  | 'impact'
  | 'trend'
  | 'comparison'
  | 'custom';

export interface AnalysisTarget {
  readonly scope: 'file' | 'directory' | 'project' | 'selection' | 'symbol' | 'diff';
  readonly uri: string;
  readonly range?: Range;
}

export interface AnalysisOptions {
  readonly depth: 'shallow' | 'standard' | 'deep';
  readonly includeHistory: boolean;
  readonly includeDependencies: boolean;
  readonly timeout: number;
}

export interface CodeAnalysisRequest {
  readonly type: CodeAnalysisType;
  readonly target: AnalysisTarget;
  readonly options: AnalysisOptions;
}

export interface AnalysisSummary {
  readonly healthScore: number;
  readonly criticalIssues: number;
  readonly totalFindings: number;
  readonly categories: Record<string, number>;
}

export interface AnalysisFinding {
  readonly id: string;
  readonly type: string;
  readonly severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  readonly title: string;
  readonly description: string;
  readonly location: Range;
  readonly evidence: string;
  readonly suggestedFix?: string;
}

export interface HalsteadMetrics {
  readonly length: number;
  readonly vocabulary: number;
  readonly volume: number;
  readonly difficulty: number;
  readonly effort: number;
  readonly timeToImplement: number;
  readonly estimatedBugs: number;
}

export interface ComplexityMetrics {
  readonly cyclomatic: number;
  readonly cognitive: number;
  readonly halstead: HalsteadMetrics;
}

export interface MaintainabilityMetrics {
  readonly index: number;
  readonly linesOfCode: number;
  readonly commentRatio: number;
  readonly testCoverage?: number;
}

export interface DuplicateBlock {
  readonly ranges: Range[];
  readonly lineCount: number;
  readonly tokenCount: number;
}

export interface DuplicationMetrics {
  readonly percentage: number;
  readonly blocks: DuplicateBlock[];
}

export interface CodeMetrics {
  readonly complexity: ComplexityMetrics;
  readonly maintainability: MaintainabilityMetrics;
  readonly duplication: DuplicationMetrics;
}

export interface CodeAnalysisResult {
  readonly summary: AnalysisSummary;
  readonly findings: AnalysisFinding[];
  readonly metrics: CodeMetrics;
  readonly recommendations: string[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §35 AI MODEL MANAGEMENT — providers, routing, performance
// ═══════════════════════════════════════════════════════════════════════════════

export type AIProviderType =
  | 'anthropic'
  | 'openai'
  | 'google'
  | 'microsoft'
  | 'meta'
  | 'cohere'
  | 'huggingface'
  | 'local'
  | 'custom';

export interface AIModelSpecs {
  readonly maxContextLength: number;
  readonly maxOutputTokens: number;
  readonly inputTypes: string[];
  readonly outputTypes: string[];
  readonly capabilities: string[];
  readonly languages: string[];
}

export interface AIModelConfig {
  readonly temperature: number;
  readonly topP: number;
  readonly topK?: number;
  readonly maxTokens: number;
  readonly stopSequences: string[];
  readonly frequencyPenalty?: number;
  readonly presencePenalty?: number;
}

export interface ModelPerformanceMetrics {
  readonly latencyP50Ms: number;
  readonly latencyP95Ms: number;
  readonly latencyP99Ms: number;
  readonly tokensPerSecond: number;
  readonly qualityScore: number;
  readonly reliabilityScore: number;
  readonly costPerMToken: number;
}

export type RoutingStrategy =
  | 'performance'
  | 'cost'
  | 'quality'
  | 'availability'
  | 'round-robin'
  | 'weighted'
  | 'custom';

export interface CircuitBreakerConfig {
  readonly failureThreshold: number;
  readonly resetTimeoutMs: number;
  readonly halfOpenRequests: number;
}

export interface AIModelRoutingConfig {
  readonly strategy: RoutingStrategy;
  readonly fallback: AIModelId[];
  readonly circuitBreaker: CircuitBreakerConfig;
}

export interface AIModelState {
  readonly id: AIModelId;
  readonly provider: AIProviderType;
  readonly name: string;
  readonly version: string;
  readonly specs: AIModelSpecs;
  readonly config: AIModelConfig;
  readonly status: 'available' | 'loading' | 'ready' | 'busy' | 'error' | 'offline';
  readonly performance: ModelPerformanceMetrics;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §36 AI CACHE — entries, stats, eviction
// ═══════════════════════════════════════════════════════════════════════════════

export interface AICacheEntry {
  readonly id: CacheEntryId;
  readonly key: string;
  readonly request: AIRequest;
  readonly response: AIResponse;
  readonly timestamp: number;
  readonly ttl: number;
  readonly accessCount: number;
  readonly lastAccessed: number;
  readonly quality: number;
  readonly size: number;
}

export interface AICacheStats {
  readonly hitRate: number;
  readonly missRate: number;
  readonly totalHits: number;
  readonly totalMisses: number;
  readonly totalRequests: number;
  readonly averageResponseTimeMs: number;
  readonly cacheSize: number;
  readonly evictionCount: number;
}

export interface AICacheConfig {
  readonly maxEntries: number;
  readonly maxSizeMb: number;
  readonly defaultTtlMs: number;
  readonly similarityThreshold: number;
  readonly evictionPolicy: 'lru' | 'lfu' | 'usefulness' | 'ttl';
  readonly compressionEnabled: boolean;
}

export interface AICacheState {
  readonly entries: Map<string, AICacheEntry>;
  readonly stats: AICacheStats;
  readonly config: AICacheConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §37 AI PERSONALIZATION — profiles, behavior, learning
// ═══════════════════════════════════════════════════════════════════════════════

export interface CodingStyleProfile {
  readonly naming: 'camelCase' | 'snake_case' | 'PascalCase' | 'kebab-case';
  readonly indentation: 'tabs' | 'spaces-2' | 'spaces-4';
  readonly quoteStyle: 'single' | 'double';
  readonly semicolons: boolean;
  readonly trailingComma: 'none' | 'es5' | 'all';
  readonly lineLength: number;
}

export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export interface UserPreferences {
  readonly verbosity: 'minimal' | 'standard' | 'detailed';
  readonly explainCode: boolean;
  readonly suggestTests: boolean;
  readonly autoComplete: boolean;
}

export interface UserProfile {
  readonly codingStyle: CodingStyleProfile;
  readonly skillLevel: SkillLevel;
  readonly preferences: UserPreferences;
  readonly competencies: Record<string, number>;
}

export interface BehaviorPattern {
  readonly type: 'temporal' | 'coding' | 'workflow' | 'collaboration';
  readonly pattern: string;
  readonly frequency: number;
  readonly confidence: number;
  readonly lastSeen: number;
}

export interface LearningEvent {
  readonly type: string;
  readonly context: string;
  readonly outcome: 'accepted' | 'rejected' | 'modified' | 'ignored';
  readonly timestamp: number;
  readonly feedback?: string;
}

export interface AdaptationConfig {
  readonly learningRate: number;
  readonly adaptationSpeed: 'slow' | 'medium' | 'fast';
  readonly privacyLevel: 'minimal' | 'standard' | 'strict';
}

export interface PersonalizationState {
  readonly userId: string;
  readonly profile: UserProfile;
  readonly behaviorPatterns: BehaviorPattern[];
  readonly learningHistory: LearningEvent[];
  readonly adaptationConfig: AdaptationConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §38 AI SECURITY — validation, scanning, compliance
// ═══════════════════════════════════════════════════════════════════════════════

export interface InputValidationConfig {
  readonly detectInjection: boolean;
  readonly detectPromptManipulation: boolean;
  readonly detectDataExtraction: boolean;
  readonly maxInputLength: number;
  readonly blockedPatterns: string[];
}

export interface OutputFilteringConfig {
  readonly filterSensitiveData: boolean;
  readonly filterCompliance: boolean;
  readonly maxOutputLength: number;
  readonly contentFilters: string[];
}

export interface CodeScanningConfig {
  readonly detectSqlInjection: boolean;
  readonly detectXss: boolean;
  readonly detectPathTraversal: boolean;
  readonly detectHardcodedSecrets: boolean;
  readonly detectUnsafeOps: boolean;
  readonly customRules: string[];
}

export type ComplianceFramework = 'gdpr' | 'ccpa' | 'sox' | 'hipaa' | 'iso27001';
export type DataClassification = 'public' | 'internal' | 'confidential' | 'restricted';

export interface ComplianceConfig {
  readonly frameworks: ComplianceFramework[];
  readonly dataClassification: DataClassification;
  readonly piiDetection: boolean;
  readonly retentionDays: number;
}

export interface AIAuditConfig {
  readonly enabled: boolean;
  readonly logLevel: 'minimal' | 'standard' | 'verbose';
  readonly retentionDays: number;
  readonly includeInputs: boolean;
  readonly includeOutputs: boolean;
}

export interface AIAuditEntry {
  readonly id: string;
  readonly requestId: AIRequestId;
  readonly action: string;
  readonly timestamp: number;
  readonly userId: string;
  readonly riskLevel: 'low' | 'medium' | 'high' | 'critical';
  readonly outcome: 'allowed' | 'blocked' | 'flagged';
  readonly details: Record<string, unknown>;
}

export interface AISecurityConfig {
  readonly inputValidation: InputValidationConfig;
  readonly outputFiltering: OutputFilteringConfig;
  readonly codeScanning: CodeScanningConfig;
  readonly compliance: ComplianceConfig;
  readonly audit: AIAuditConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §39 WORKSPACE — entity, layout, panels, tabs
// ═══════════════════════════════════════════════════════════════════════════════

export type LayoutType =
  | 'split'
  | 'tabbed'
  | 'grid'
  | 'floating'
  | 'mosaic'
  | 'zen'
  | 'focus';

export interface LayoutLeafData {
  readonly editorId?: EditorInstanceId;
  readonly terminalId?: TerminalId;
  readonly panelType?: string;
}

export interface LayoutNode {
  readonly id: LayoutNodeId;
  readonly type: 'split' | 'tab-group' | 'leaf';
  readonly children?: LayoutNode[];
  readonly size?: number;
  readonly direction?: 'horizontal' | 'vertical';
  readonly activeChild?: number;
  readonly data?: LayoutLeafData;
}

export interface WorkspaceLayout {
  readonly type: LayoutType;
  readonly rootNode: LayoutNode;
  readonly orientation: 'horizontal' | 'vertical';
}

export type PanelType =
  | 'explorer'
  | 'search'
  | 'git'
  | 'debug'
  | 'extensions'
  | 'terminal'
  | 'problems'
  | 'output'
  | 'console'
  | 'custom';

export type PanelPosition = 'left' | 'right' | 'bottom' | 'top';

export interface PanelState {
  readonly id: PanelId;
  readonly type: PanelType;
  readonly position: PanelPosition;
  readonly isVisible: boolean;
  readonly size: number;
  readonly isCollapsed: boolean;
}

export interface TabState {
  readonly id: TabId;
  readonly groupId: TabGroupId;
  readonly label: string;
  readonly uri?: string;
  readonly editorId?: EditorInstanceId;
  readonly isPinned: boolean;
  readonly isPreview: boolean;
  readonly isDirty: boolean;
  readonly iconPath?: string;
  readonly description?: string;
}

export interface TabGroupState {
  readonly id: TabGroupId;
  readonly tabs: TabState[];
  readonly activeTabId?: TabId;
  readonly isActive: boolean;
}

export interface WorkspaceState {
  readonly id: WorkspaceId;
  readonly name: string;
  readonly rootFolders: string[];
  readonly layout: WorkspaceLayout;
  readonly panels: PanelState[];
  readonly editors: EditorState[];
  readonly terminals: TerminalState[];
  readonly theme: string;
  readonly settings: Record<string, unknown>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §40 WORKSPACE COLLABORATION & SYNC
// ═══════════════════════════════════════════════════════════════════════════════

export interface CollabParticipant {
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly activeFile?: string;
  readonly isFollowing?: string;
}

export interface CollabCursor {
  readonly participantId: string;
  readonly fileUri: string;
  readonly position: Position;
  readonly selection?: Selection;
  readonly timestamp: number;
}

export interface FollowModeState {
  readonly leaderId: string;
  readonly followers: string[];
  readonly isActive: boolean;
}

export interface WorkspaceCollabState {
  readonly isActive: boolean;
  readonly participants: CollabParticipant[];
  readonly cursors: CollabCursor[];
  readonly followTarget?: string;
}

export interface SyncConflict {
  readonly path: string;
  readonly localVersion: number;
  readonly remoteVersion: number;
  readonly resolvedBy?: 'local' | 'remote' | 'manual';
}

export interface WorkspaceSyncState {
  readonly provider: string;
  readonly lastSyncedAt: number;
  readonly status: 'synced' | 'syncing' | 'conflict' | 'error' | 'offline';
  readonly conflicts: SyncConflict[];
  readonly selectiveSync: Record<string, boolean>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §41 GIT — repository, status, stash, config
// ═══════════════════════════════════════════════════════════════════════════════

export interface GitHead {
  readonly type: 'branch' | 'detached';
  readonly name?: BranchName;
  readonly commitHash: CommitHash;
}

export interface GitBranch {
  readonly name: BranchName;
  readonly commitHash: CommitHash;
  readonly upstream?: { readonly remote: RemoteName; readonly branch: BranchName };
  readonly isHead: boolean;
  readonly ahead: number;
  readonly behind: number;
}

export interface GitRemote {
  readonly name: RemoteName;
  readonly fetchUrl: string;
  readonly pushUrl: string;
}

export interface GitTag {
  readonly name: TagName;
  readonly commitHash: CommitHash;
  readonly message?: string;
  readonly tagger?: string;
  readonly date?: number;
  readonly isAnnotated: boolean;
}

export interface GitStash {
  readonly id: StashId;
  readonly message: string;
  readonly branch?: BranchName;
  readonly date: number;
}

export type GitStatusCode =
  | 'unmodified'
  | 'modified'
  | 'added'
  | 'deleted'
  | 'renamed'
  | 'copied'
  | 'untracked'
  | 'ignored'
  | 'unmerged';

export interface GitFileStatus {
  readonly path: string;
  readonly indexStatus: GitStatusCode;
  readonly workTreeStatus: GitStatusCode;
  readonly isSubmodule: boolean;
}

export interface GitConfig {
  readonly userName?: string;
  readonly userEmail?: string;
  readonly signingKey?: string;
  readonly autoSign: boolean;
  readonly defaultBranch: string;
  readonly mergeStrategy: 'recursive' | 'ours' | 'theirs' | 'octopus';
}

export interface GitRepositoryState {
  readonly id: GitRepositoryId;
  readonly rootPath: string;
  readonly head: GitHead;
  readonly branches: GitBranch[];
  readonly remotes: GitRemote[];
  readonly tags: GitTag[];
  readonly stashes: GitStash[];
  readonly status: GitFileStatus[];
  readonly config: GitConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §42 GIT — commits, blame, bisect
// ═══════════════════════════════════════════════════════════════════════════════

export interface GitPerson {
  readonly name: string;
  readonly email: string;
  readonly date: number;
}

export interface GitCommit {
  readonly hash: CommitHash;
  readonly parentHashes: CommitHash[];
  readonly author: GitPerson;
  readonly committer: GitPerson;
  readonly message: string;
  readonly date: number;
  readonly isSigned: boolean;
}

export interface GitLogOptions {
  readonly maxCount?: number;
  readonly skip?: number;
  readonly since?: number;
  readonly until?: number;
  readonly author?: string;
  readonly grep?: string;
  readonly path?: string;
  readonly firstParent?: boolean;
}

export interface GitBlameLine {
  readonly lineNumber: number;
  readonly commitHash: CommitHash;
  readonly author: string;
  readonly date: number;
  readonly content: string;
}

export interface GitBlameResult {
  readonly lines: GitBlameLine[];
}

export interface GitBisectState {
  readonly status: 'inactive' | 'active' | 'found';
  readonly good: CommitHash[];
  readonly bad: CommitHash[];
  readonly current?: CommitHash;
  readonly remaining: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §43 GIT — staging, merge, hooks, providers
// ═══════════════════════════════════════════════════════════════════════════════

export interface GitDiffLine {
  readonly type: 'context' | 'add' | 'delete';
  readonly content: string;
  readonly oldLineNumber?: number;
  readonly newLineNumber?: number;
}

export interface GitStagingHunk {
  readonly header: string;
  readonly oldStart: number;
  readonly oldCount: number;
  readonly newStart: number;
  readonly newCount: number;
  readonly lines: GitDiffLine[];
}

export interface GitConflict {
  readonly path: string;
  readonly baseContent: string;
  readonly oursContent: string;
  readonly theirsContent: string;
  readonly isResolved: boolean;
}

export interface GitMergeState {
  readonly status: 'none' | 'merging' | 'rebasing' | 'cherry-picking' | 'reverting';
  readonly source?: BranchName;
  readonly conflicts: GitConflict[];
}

export type GitHookName =
  | 'pre-commit'
  | 'prepare-commit-msg'
  | 'commit-msg'
  | 'post-commit'
  | 'pre-rebase'
  | 'post-rewrite'
  | 'post-checkout'
  | 'post-merge'
  | 'pre-push'
  | 'pre-auto-gc';

export interface GitHookState {
  readonly id: HookId;
  readonly name: GitHookName;
  readonly path: string;
  readonly isEnabled: boolean;
  readonly isExecutable: boolean;
}

export type GitProviderType = 'github' | 'gitlab' | 'gitea' | 'bitbucket' | 'local';

export interface GitProviderConfig {
  readonly type: GitProviderType;
  readonly apiUrl: string;
  readonly token?: string;
  readonly username?: string;
}

export interface PRReviewer {
  readonly name: string;
  readonly state: 'pending' | 'approved' | 'changes-requested' | 'commented';
}

export interface PRCheck {
  readonly name: string;
  readonly status: 'queued' | 'in-progress' | 'completed';
  readonly conclusion?: 'success' | 'failure' | 'neutral' | 'cancelled' | 'timed-out';
}

export interface PullRequestState {
  readonly id: string;
  readonly number: number;
  readonly title: string;
  readonly body: string;
  readonly state: 'open' | 'closed' | 'merged';
  readonly author: string;
  readonly sourceBranch: BranchName;
  readonly targetBranch: BranchName;
  readonly reviewers: PRReviewer[];
  readonly checks: PRCheck[];
  readonly labels: string[];
  readonly createdAt: number;
  readonly updatedAt: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §44 FILE SYSTEM — files, directories, watching, encoding
// ═══════════════════════════════════════════════════════════════════════════════

export interface FileEntry {
  readonly id: FileId;
  readonly path: string;
  readonly name: string;
  readonly extension: string;
  readonly size: number;
  readonly encoding: DocumentEncoding;
  readonly mimeType: string;
  readonly isReadOnly: boolean;
  readonly isBinary: boolean;
  readonly modifiedAt: number;
  readonly createdAt: number;
}

export interface DirectoryEntry {
  readonly id: DirectoryId;
  readonly path: string;
  readonly name: string;
  readonly children: (FileEntry | DirectoryEntry)[];
  readonly isExpanded: boolean;
}

export interface FileWatchEvent {
  readonly type: 'create' | 'change' | 'delete' | 'rename';
  readonly path: string;
  readonly oldPath?: string;
  readonly timestamp: number;
}

export interface FileWatchConfig {
  readonly paths: string[];
  readonly recursive: boolean;
  readonly ignorePatterns: string[];
  readonly debounceMs: number;
  readonly usePolling: boolean;
}

export interface FileStat {
  readonly size: number;
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly isSymlink: boolean;
  readonly modifiedAt: number;
  readonly createdAt: number;
  readonly permissions: number;
}

export type GlobPattern = string;

export interface LargeFileConfig {
  readonly threshold: number;
  readonly streamChunkSize: number;
  readonly virtualScrollThreshold: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §45 FILE SYSTEM — AST, search, indexing, collaboration
// ═══════════════════════════════════════════════════════════════════════════════

export interface ASTNode {
  readonly type: string;
  readonly range: Range;
  readonly children: ASTNode[];
  readonly parent?: string;
  readonly name?: string;
  readonly value?: unknown;
  readonly language: LanguageId;
}

export interface ASTQuery {
  readonly pattern: string;
  readonly language: LanguageId;
  readonly captureNames?: string[];
}

export interface ASTTransform {
  readonly query: ASTQuery;
  readonly replacement: string | ((node: ASTNode) => string);
}

export interface SymbolInfo {
  readonly name: string;
  readonly kind: SymbolKind;
  readonly range: Range;
  readonly selectionRange: Range;
  readonly containerName?: string;
  readonly uri: string;
}

export interface SearchQuery {
  readonly pattern: string;
  readonly isRegex: boolean;
  readonly isCaseSensitive: boolean;
  readonly isWholeWord: boolean;
  readonly includePattern?: GlobPattern;
  readonly excludePattern?: GlobPattern;
  readonly maxResults?: number;
}

export interface SearchMatch {
  readonly range: Range;
  readonly preview: string;
  readonly lineNumber: number;
}

export interface SearchResult {
  readonly id: SearchResultId;
  readonly uri: string;
  readonly matches: SearchMatch[];
}

export interface SearchReplaceRequest {
  readonly query: SearchQuery;
  readonly replacement: string;
  readonly preserveCase: boolean;
}

export interface FileIndex {
  readonly id: IndexId;
  readonly type: 'file' | 'symbol' | 'fulltext';
  readonly status: 'building' | 'ready' | 'updating' | 'error';
  readonly fileCount: number;
  readonly lastUpdated: number;
}

export interface OTOperation {
  readonly type: 'insert' | 'delete' | 'retain';
  readonly position: number;
  readonly text?: string;
  readonly count?: number;
  readonly clientId: string;
  readonly revision: number;
}

export interface CollabDocument {
  readonly uri: string;
  readonly revision: number;
  readonly pendingOps: OTOperation[];
  readonly acknowledgedOps: OTOperation[];
  readonly clients: string[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §46 PROJECT — entity, tasks, dependencies, environments
// ═══════════════════════════════════════════════════════════════════════════════

export type ProjectType = 'single' | 'monorepo' | 'multi-root';

export type PackageManagerType =
  | 'npm'
  | 'yarn'
  | 'pnpm'
  | 'bun'
  | 'cargo'
  | 'go'
  | 'pip'
  | 'poetry'
  | 'gradle'
  | 'maven'
  | 'unknown';

export type TaskSource =
  | 'package.json'
  | 'makefile'
  | 'taskfile'
  | 'justfile'
  | 'cargo'
  | 'gradle'
  | 'custom';

export interface ProjectTask {
  readonly id: TaskId;
  readonly name: string;
  readonly source: TaskSource;
  readonly command: string;
  readonly cwd: string;
  readonly dependencies: TaskId[];
  readonly status: 'idle' | 'running' | 'success' | 'failure' | 'cancelled';
}

export type BuildSystem =
  | 'webpack'
  | 'vite'
  | 'esbuild'
  | 'rollup'
  | 'turbopack'
  | 'rspack'
  | 'parcel'
  | 'tsc'
  | 'swc'
  | 'custom';

export interface MonorepoPackage {
  readonly name: string;
  readonly path: string;
  readonly version: string;
  readonly dependencies: string[];
}

export interface MonorepoConfig {
  readonly tool: 'npm-workspaces' | 'yarn-workspaces' | 'pnpm-workspaces' | 'turborepo' | 'nx' | 'lerna';
  readonly workspaces: string[];
  readonly packages: MonorepoPackage[];
}

export interface CIConfig {
  readonly provider: 'github-actions' | 'gitlab-ci' | 'jenkins' | 'circleci' | 'travis' | 'azure-pipelines' | 'custom';
  readonly configPath: string;
}

export interface ProjectConfig {
  readonly buildSystem?: BuildSystem;
  readonly monorepo?: MonorepoConfig;
  readonly ci?: CIConfig;
}

export interface VulnerabilityInfo {
  readonly id: string;
  readonly severity: 'info' | 'low' | 'moderate' | 'high' | 'critical';
  readonly title: string;
  readonly description: string;
  readonly fixAvailable?: string;
}

export interface DependencyInfo {
  readonly id: DependencyId;
  readonly name: string;
  readonly version: string;
  readonly type: 'production' | 'development' | 'peer' | 'optional';
  readonly isDirectDep: boolean;
  readonly license?: string;
  readonly vulnerabilities: VulnerabilityInfo[];
}

export interface DependencyGraph {
  readonly nodes: Map<string, DependencyInfo>;
  readonly edges: Map<string, string[]>;
  readonly hasCircular: boolean;
  readonly circularPaths: string[][];
}

export interface EnvironmentState {
  readonly id: EnvironmentId;
  readonly name: string;
  readonly variables: Record<string, string>;
  readonly isActive: boolean;
  readonly source: string;
  readonly hasSecrets: boolean;
}

export interface ProjectState {
  readonly id: ProjectId;
  readonly rootPath: string;
  readonly name: string;
  readonly type: ProjectType;
  readonly packageManager: PackageManagerType;
  readonly tasks: ProjectTask[];
  readonly config: ProjectConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §47 PLUGINS — entity, manifest, lifecycle, sandbox
// ═══════════════════════════════════════════════════════════════════════════════

export type PluginStatus =
  | 'discovered'
  | 'installed'
  | 'activating'
  | 'active'
  | 'deactivating'
  | 'disabled'
  | 'error'
  | 'updating'
  | 'uninstalling';

export type PluginPermission =
  | 'fs.read'
  | 'fs.write'
  | 'network'
  | 'process'
  | 'clipboard'
  | 'storage'
  | 'ui.notifications'
  | 'ui.webview'
  | 'terminal'
  | 'git'
  | 'debug'
  | 'ai';

export interface MenuContribution {
  readonly command: CommandId;
  readonly group?: string;
  readonly when?: WhenClause;
}

export interface ViewContribution {
  readonly id: string;
  readonly name: string;
  readonly when?: WhenClause;
  readonly icon?: string;
}

export interface ThemeContribution {
  readonly label: string;
  readonly uiTheme: 'vs' | 'vs-dark' | 'hc-black' | 'hc-light';
  readonly path: string;
}

export interface LanguageContribution {
  readonly id: LanguageId;
  readonly extensions?: string[];
  readonly filenames?: string[];
  readonly configuration?: string;
}

export interface SnippetContribution {
  readonly language: LanguageId;
  readonly path: string;
}

export interface GrammarContribution {
  readonly language: LanguageId;
  readonly scopeName: string;
  readonly path: string;
}

export interface DebuggerContribution {
  readonly type: string;
  readonly label: string;
  readonly program?: string;
  readonly runtime?: string;
  readonly languages?: LanguageId[];
}

export interface FormatterContribution {
  readonly language: LanguageId;
  readonly command: CommandId;
}

export interface SettingSchema {
  readonly type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'enum';
  readonly default: unknown;
  readonly description: string;
  readonly enum?: unknown[];
  readonly enumDescriptions?: string[];
  readonly minimum?: number;
  readonly maximum?: number;
  readonly pattern?: string;
  readonly scope: SettingsScope;
  readonly tags?: string[];
  readonly deprecationMessage?: string;
}

export interface SettingsContribution {
  readonly title: string;
  readonly properties: Record<string, SettingSchema>;
}

export interface PluginContributions {
  readonly commands?: CommandData[];
  readonly menus?: MenuContribution[];
  readonly keybindings?: KeybindingData[];
  readonly views?: ViewContribution[];
  readonly themes?: ThemeContribution[];
  readonly languages?: LanguageContribution[];
  readonly snippets?: SnippetContribution[];
  readonly grammars?: GrammarContribution[];
  readonly debuggers?: DebuggerContribution[];
  readonly formatters?: FormatterContribution[];
  readonly settings?: SettingsContribution[];
}

export interface PluginManifest {
  readonly id: PluginManifestId;
  readonly name: string;
  readonly version: string;
  readonly displayName: string;
  readonly description: string;
  readonly author: string;
  readonly license: string;
  readonly repository?: string;
  readonly main: string;
  readonly activationEvents: string[];
  readonly contributes: PluginContributions;
  readonly permissions: PluginPermission[];
  readonly dependencies: Record<string, string>;
}

export interface PluginResourceUsage {
  readonly cpuMs: number;
  readonly memoryMb: number;
  readonly activationTimeMs: number;
  readonly apiCalls: number;
}

export interface PluginState {
  readonly id: PluginId;
  readonly manifest: PluginManifest;
  readonly status: PluginStatus;
  readonly activationEvents: string[];
  readonly contributions: PluginContributions;
  readonly resourceUsage: PluginResourceUsage;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §48 PLUGIN SANDBOX & MARKETPLACE
// ═══════════════════════════════════════════════════════════════════════════════

export type SandboxCapability =
  | 'dom-access'
  | 'file-read'
  | 'file-write'
  | 'network-fetch'
  | 'process-spawn'
  | 'clipboard-read'
  | 'clipboard-write'
  | 'storage-local'
  | 'storage-cloud'
  | 'terminal-create'
  | 'git-read'
  | 'git-write'
  | 'debug-attach'
  | 'ai-query';

export interface SandboxResourceLimits {
  readonly maxCpuMs: number;
  readonly maxMemoryMb: number;
  readonly maxStorageMb: number;
  readonly maxNetworkRequests: number;
  readonly maxApiCallsPerMinute: number;
}

export interface SandboxConfig {
  readonly runtime: 'worker' | 'iframe' | 'process' | 'wasm';
  readonly permissions: PluginPermission[];
  readonly resourceLimits: SandboxResourceLimits;
  readonly capabilities: SandboxCapability[];
}

export interface MarketplaceEntry {
  readonly pluginId: PluginId;
  readonly name: string;
  readonly displayName: string;
  readonly description: string;
  readonly version: string;
  readonly author: string;
  readonly publisher: string;
  readonly downloads: number;
  readonly rating: number;
  readonly reviewCount: number;
  readonly categories: string[];
  readonly tags: string[];
  readonly lastUpdated: number;
  readonly verified: boolean;
}

export interface MarketplaceSearchQuery {
  readonly text?: string;
  readonly category?: string;
  readonly sortBy: 'relevance' | 'installs' | 'rating' | 'updated';
  readonly page: number;
  readonly pageSize: number;
}

export interface MarketplaceSearchResult {
  readonly entries: MarketplaceEntry[];
  readonly totalCount: number;
  readonly page: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §49 SETTINGS — model, scopes, schema, sync
// ═══════════════════════════════════════════════════════════════════════════════

export type SettingsScope =
  | 'default'
  | 'user'
  | 'workspace'
  | 'folder'
  | 'language'
  | 'runtime';

export interface SettingsProfile {
  readonly id: SettingsProfileId;
  readonly name: string;
  readonly settings: Record<string, unknown>;
  readonly isDefault: boolean;
  readonly createdAt: number;
}

export interface SettingsState {
  readonly scopes: Map<SettingsScope, Record<string, unknown>>;
  readonly schema: Map<string, SettingSchema>;
  readonly profiles: SettingsProfile[];
  readonly activeProfileId?: SettingsProfileId;
}

export interface SettingsSyncConflict {
  readonly key: string;
  readonly localValue: unknown;
  readonly remoteValue: unknown;
  readonly resolvedValue?: unknown;
}

export interface SettingsSyncState {
  readonly provider: string;
  readonly lastSyncedAt: number;
  readonly status: 'synced' | 'syncing' | 'conflict' | 'error' | 'disabled';
  readonly enabledCategories: string[];
  readonly conflicts: SettingsSyncConflict[];
}

export interface SettingsChangeEvent {
  readonly key: string;
  readonly oldValue: unknown;
  readonly newValue: unknown;
  readonly scope: SettingsScope;
  readonly source: 'user' | 'sync' | 'extension' | 'migration';
}

export interface SettingsMigration {
  readonly fromVersion: string;
  readonly toVersion: string;
  readonly transforms: Record<string, (old: unknown) => unknown>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §50 DIFF — computation, rendering, merge
// ═══════════════════════════════════════════════════════════════════════════════

export type DiffAlgorithm = 'myers' | 'patience' | 'histogram';

export interface CharRange {
  readonly startColumn: number;
  readonly endColumn: number;
}

export interface DiffLine {
  readonly type: 'context' | 'add' | 'delete' | 'equal';
  readonly content: string;
  readonly oldLineNumber?: number;
  readonly newLineNumber?: number;
  readonly charChanges?: CharRange[];
}

export interface DiffHunk {
  readonly id: HunkId;
  readonly oldStart: number;
  readonly oldCount: number;
  readonly newStart: number;
  readonly newCount: number;
  readonly lines: DiffLine[];
  readonly isConflict: boolean;
}

export interface DiffStats {
  readonly additions: number;
  readonly deletions: number;
  readonly changes: number;
  readonly fileCount?: number;
}

export type DiffRenderMode = 'side-by-side' | 'inline' | 'unified';

export interface MovedBlock {
  readonly originalRange: Range;
  readonly modifiedRange: Range;
  readonly similarity: number;
}

export interface DiffResult {
  readonly id: DiffId;
  readonly algorithm: DiffAlgorithm;
  readonly original: string;
  readonly modified: string;
  readonly hunks: DiffHunk[];
  readonly stats: DiffStats;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §51 DIFF — three-way merge, code review
// ═══════════════════════════════════════════════════════════════════════════════

export type MergeResolutionStrategy =
  | 'accept-local'
  | 'accept-remote'
  | 'accept-both'
  | 'custom';

export interface MergeConflict {
  readonly id: string;
  readonly range: Range;
  readonly baseContent: string;
  readonly localContent: string;
  readonly remoteContent: string;
  readonly resolution?: MergeResolutionStrategy;
}

export interface MergeResolution {
  readonly conflictId: string;
  readonly strategy: MergeResolutionStrategy;
  readonly content: string;
}

export interface ThreeWayMergeState {
  readonly base: string;
  readonly local: string;
  readonly remote: string;
  readonly result: string;
  readonly conflicts: MergeConflict[];
  readonly autoResolutions: MergeResolution[];
}

export interface CodeSuggestion {
  readonly originalCode: string;
  readonly suggestedCode: string;
  readonly isApplied: boolean;
}

export interface ReviewComment {
  readonly id: ReviewCommentId;
  readonly threadId: ReviewThreadId;
  readonly authorId: string;
  readonly body: string;
  readonly timestamp: number;
  readonly suggestion?: CodeSuggestion;
  readonly reactions: Record<string, number>;
}

export interface ReviewThread {
  readonly id: ReviewThreadId;
  readonly fileUri: string;
  readonly range: Range;
  readonly comments: ReviewComment[];
  readonly status: 'open' | 'resolved' | 'outdated';
  readonly createdAt: number;
}

export interface ReviewState {
  readonly status: 'draft' | 'pending' | 'submitted' | 'approved' | 'changes-requested' | 'commented';
  readonly threads: ReviewThread[];
  readonly summary?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §52 ADAPTERS — Monaco, LSP, DAP, tools
// ═══════════════════════════════════════════════════════════════════════════════

export interface MonacoAdapterConfig {
  readonly editor: unknown;
  readonly diffEditor?: unknown;
  readonly services?: Record<string, unknown>;
}

export interface MonacoCommandMapping {
  readonly editorCommand: CommandId;
  readonly monacoCommand: string;
  readonly bidirectional: boolean;
}

export interface LSPClientCapabilities {
  readonly textDocument: boolean;
  readonly workspace: boolean;
  readonly window: boolean;
  readonly general: boolean;
}

export interface LSPClientConfig {
  readonly serverCommand: string;
  readonly serverArgs: string[];
  readonly initializationOptions?: unknown;
  readonly capabilities: LSPClientCapabilities;
  readonly rootUri: string;
  readonly workspaceFolders?: string[];
}

export interface LSPRequestMapping {
  readonly method: string;
  readonly params: unknown;
  readonly resultType: string;
}

export interface DAPClientConfig {
  readonly type: string;
  readonly request: 'launch' | 'attach';
  readonly program?: string;
  readonly args?: string[];
  readonly cwd?: string;
  readonly env?: Record<string, string>;
  readonly port?: number;
}

export interface DAPBreakpoint {
  readonly id: string;
  readonly location: { readonly uri: string; readonly line: number; readonly column?: number };
  readonly condition?: string;
  readonly hitCondition?: string;
  readonly logMessage?: string;
  readonly isVerified: boolean;
}

export interface DAPScope {
  readonly name: string;
  readonly variablesReference: number;
  readonly expensive: boolean;
}

export interface DAPStackFrame {
  readonly id: number;
  readonly name: string;
  readonly source: { readonly uri: string; readonly line: number };
  readonly line: number;
  readonly column: number;
  readonly scopes: DAPScope[];
}

export interface DAPVariable {
  readonly name: string;
  readonly value: string;
  readonly type?: string;
  readonly variablesReference: number;
  readonly namedVariables?: number;
  readonly indexedVariables?: number;
}

export interface ExternalToolConfig {
  readonly name: string;
  readonly command: string;
  readonly args: string[];
  readonly languages: LanguageId[];
  readonly formatOnSave: boolean;
  readonly lintOnSave: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §53 GLOBAL — events, storage, performance, permissions
// ═══════════════════════════════════════════════════════════════════════════════

export interface EventChannel {
  readonly id: EventChannelId;
  readonly name: string;
  readonly pattern: string;
  readonly listeners: number;
  readonly lastEvent?: number;
}

export interface EditorEventBus {
  readonly channels: Map<EventChannelId, EventChannel>;
}

export interface EditorEventData {
  readonly channel: EventChannelId;
  readonly type: string;
  readonly payload: unknown;
  readonly timestamp: number;
  readonly source: string;
  readonly priority: 'low' | 'normal' | 'high';
}

export interface StorageEntry {
  readonly namespace: StorageNamespace;
  readonly key: string;
  readonly value: unknown;
  readonly expiresAt?: number;
  readonly encrypted: boolean;
}

export type StorageBackendType = 'memory' | 'localStorage' | 'indexedDB' | 'file' | 'cloud';

export interface StorageConfig {
  readonly backend: StorageBackendType;
  readonly namespace: StorageNamespace;
  readonly encryptionKey?: string;
  readonly maxSizeMb: number;
}

export interface PerformanceSnapshot {
  readonly timestamp: number;
  readonly fps: number;
  readonly frameTimeMs: number;
  readonly longTasks: number;
  readonly memoryUsageMb: number;
  readonly gcPauseMs: number;
  readonly renderQueueDepth: number;
}

export interface PerformanceBudget {
  readonly maxFrameTimeMs: number;
  readonly targetFps: number;
  readonly maxMemoryMb: number;
  readonly maxLongTaskMs: number;
}

export type TrustLevel = 'trusted' | 'workspace' | 'untrusted';

export interface PermissionGrant {
  readonly id: PermissionId;
  readonly scope: string;
  readonly capability: string;
  readonly trustLevel: TrustLevel;
  readonly grantedAt: number;
  readonly expiresAt?: number;
}

export interface PermissionRequest {
  readonly capability: string;
  readonly scope: string;
  readonly requestedBy: string;
  readonly reason: string;
}

export interface LogEntry {
  readonly channel: LogChannelId;
  readonly level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  readonly message: string;
  readonly timestamp: number;
  readonly data?: unknown;
  readonly source: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §54 IO CONFIGS — per-module and cross-cutting
// ═══════════════════════════════════════════════════════════════════════════════

// --- Per-Module IO Configs ---

export interface DocumentIOConfig {
  readonly pieceTableBackend: 'array' | 'rope' | 'piece-table';
  readonly defaultEncoding: DocumentEncoding;
  readonly maxFileSizeBytes: number;
  readonly autoSaveIntervalMs: number;
  readonly snapshotIntervalMs: number;
}

export interface SyntaxIOConfig {
  readonly grammarSources: string[];
  readonly tokenCacheSize: number;
  readonly incrementalThreshold: number;
  readonly maxTokenizationLineLength: number;
}

export interface LanguageIOConfig {
  readonly lspConnections: Record<string, LSPClientConfig>;
  readonly completionDebounceMs: number;
  readonly diagnosticDelayMs: number;
  readonly hoverDelayMs: number;
  readonly signatureHelpTriggerCharacters: string[];
}

export interface LayoutIOConfig {
  readonly viewportDimensions: { readonly width: number; readonly height: number };
  readonly scrollBehavior: ScrollConfig;
  readonly minimapSettings: MinimapConfig;
  readonly gutterSettings: GutterConfig;
}

export interface EditorIOConfig {
  readonly vimModeEnabled: boolean;
  readonly keybindingScheme: 'default' | 'vim' | 'emacs' | 'sublime' | 'vscode';
  readonly accessibilityLevel: 'standard' | 'enhanced' | 'screen-reader';
  readonly defaultConfig: EditorConfig;
}

export interface TerminalIOConfig {
  readonly defaultShell: ShellType;
  readonly ptyBackend: 'node-pty' | 'wasm' | 'mock';
  readonly bufferLimits: ScrollbackConfig;
  readonly recordingStoragePath?: string;
  readonly maxTerminals: number;
}

export interface AIIOConfig {
  readonly modelProviders: Record<string, { readonly apiUrl: string; readonly apiKey?: string }>;
  readonly cacheBackend: StorageBackendType;
  readonly privacyLevel: 'minimal' | 'standard' | 'strict';
  readonly defaultModel?: AIModelId;
  readonly maxConcurrentRequests: number;
}

export interface WorkspaceIOConfig {
  readonly persistenceBackend: StorageBackendType;
  readonly syncProvider?: string;
  readonly layoutDefaults: LayoutType;
  readonly maxRecentWorkspaces: number;
}

export interface GitIOConfig {
  readonly binaryPath: string;
  readonly credentialHelper?: string;
  readonly signingKey?: string;
  readonly defaultRemote: string;
  readonly autoFetch: boolean;
  readonly autoFetchIntervalMs: number;
}

export interface FileSystemIOConfig {
  readonly watcherBackend: 'native' | 'polling' | 'chokidar';
  readonly indexingStrategy: 'eager' | 'lazy' | 'background';
  readonly largeFileThreshold: number;
  readonly maxWatchedFiles: number;
  readonly ignorePatterns: string[];
}

export interface ProjectIOConfig {
  readonly taskRunner: 'integrated' | 'external';
  readonly buildSystem?: BuildSystem;
  readonly monorepoTool?: string;
  readonly envFilePaths: string[];
}

export interface PluginsIOConfig {
  readonly sandboxRuntime: 'worker' | 'iframe' | 'process' | 'wasm';
  readonly marketplaceRegistryUrl?: string;
  readonly resourceLimits: SandboxResourceLimits;
  readonly autoUpdate: boolean;
  readonly trustedPublishers: string[];
}

export interface SettingsIOConfig {
  readonly storageBackend: StorageBackendType;
  readonly syncProvider?: string;
  readonly migrationHandlers: SettingsMigration[];
  readonly importExportFormat: 'json' | 'yaml';
}

export interface DiffIOConfig {
  readonly algorithmPreference: DiffAlgorithm;
  readonly contextLines: number;
  readonly maxFileSizeBytes: number;
  readonly ignoreWhitespace: boolean;
}

export interface AdaptersIOConfig {
  readonly monacoInstance?: unknown;
  readonly lspSocketFactory?: (config: LSPClientConfig) => unknown;
  readonly dapSocketFactory?: (config: DAPClientConfig) => unknown;
  readonly externalToolPaths: Record<string, string>;
}

export interface GlobalIOConfig {
  readonly eventBusCapacity: number;
  readonly storageBackend: StorageBackendType;
  readonly logLevel: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  readonly performanceBudget: PerformanceBudget;
  readonly telemetryEnabled: boolean;
}

// --- Cross-Cutting Composite Configs ---

export interface FullEditorIOConfig {
  readonly document: DocumentIOConfig;
  readonly syntax: SyntaxIOConfig;
  readonly language: LanguageIOConfig;
  readonly layout: LayoutIOConfig;
  readonly editor: EditorIOConfig;
}

export interface TerminalSuiteIOConfig {
  readonly terminal: TerminalIOConfig;
  readonly ai: AIIOConfig;
}

export interface AIFullIOConfig {
  readonly ai: AIIOConfig;
  readonly security: AISecurityConfig;
  readonly cache: AICacheConfig;
}

export interface CollaborationIOConfig {
  readonly workspaceCollab: WorkspaceIOConfig;
  readonly terminal: TerminalIOConfig;
  readonly filesystem: FileSystemIOConfig;
  readonly git: GitIOConfig;
}

export interface DevWorkflowIOConfig {
  readonly git: GitIOConfig;
  readonly project: ProjectIOConfig;
  readonly diff: DiffIOConfig;
  readonly plugins: PluginsIOConfig;
}

export interface CodeIntelligenceIOConfig {
  readonly language: LanguageIOConfig;
  readonly syntax: SyntaxIOConfig;
  readonly filesystem: FileSystemIOConfig;
  readonly ai: AIIOConfig;
}

// --- Dependency Injection ---

export interface LSPTransport {
  readonly send: (method: string, params: unknown) => Promise<unknown>;
  readonly onNotification: (method: string, handler: (params: unknown) => void) => void;
  readonly dispose: () => void;
}

export interface DAPTransport {
  readonly send: (command: string, args: unknown) => Promise<unknown>;
  readonly onEvent: (event: string, handler: (body: unknown) => void) => void;
  readonly dispose: () => void;
}

export interface PTYBackend {
  readonly spawn: (config: PTYConfig) => { readonly pid: number; readonly processId: PTYProcessId };
  readonly write: (processId: PTYProcessId, data: string) => void;
  readonly resize: (processId: PTYProcessId, cols: number, rows: number) => void;
  readonly kill: (processId: PTYProcessId, signal?: PTYSignal) => void;
  readonly onData: (processId: PTYProcessId, handler: (data: string) => void) => void;
  readonly onExit: (processId: PTYProcessId, handler: (code: number, signal?: number) => void) => void;
}

export interface StorageBackend {
  readonly get: (namespace: StorageNamespace, key: string) => Promise<unknown>;
  readonly set: (namespace: StorageNamespace, key: string, value: unknown) => Promise<void>;
  readonly delete: (namespace: StorageNamespace, key: string) => Promise<void>;
  readonly list: (namespace: StorageNamespace) => Promise<string[]>;
  readonly clear: (namespace: StorageNamespace) => Promise<void>;
}

export interface SyncProvider {
  readonly push: (data: Record<string, unknown>) => Promise<void>;
  readonly pull: () => Promise<Record<string, unknown>>;
  readonly resolve: (conflicts: SyncConflict[]) => Promise<SyncConflict[]>;
  readonly status: () => Promise<'synced' | 'syncing' | 'conflict' | 'error' | 'offline'>;
}

export interface AIProviderConnection {
  readonly send: (request: AIRequest) => Promise<AIResponse>;
  readonly stream: (request: AIRequest, onChunk: (chunk: AIContent) => void) => Promise<AIResponse>;
  readonly cancel: (requestId: AIRequestId) => void;
  readonly status: () => 'connected' | 'disconnected' | 'error';
}

export interface FileSystemBackend {
  readonly readFile: (path: string) => Promise<string>;
  readonly writeFile: (path: string, content: string) => Promise<void>;
  readonly deleteFile: (path: string) => Promise<void>;
  readonly readDir: (path: string) => Promise<string[]>;
  readonly stat: (path: string) => Promise<FileStat>;
  readonly watch: (paths: string[], handler: (event: FileWatchEvent) => void) => { readonly dispose: () => void };
}

export interface IODependencies {
  readonly monaco?: unknown;
  readonly xterm?: unknown;
  readonly playwright?: unknown;
  readonly lspClient?: LSPTransport;
  readonly dapClient?: DAPTransport;
  readonly ptyBackend?: PTYBackend;
  readonly storage?: StorageBackend;
  readonly syncProvider?: SyncProvider;
  readonly aiProviders?: Map<string, AIProviderConnection>;
  readonly webSocket?: unknown;
  readonly fs?: FileSystemBackend;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-1 SELECTION STATE — multi-cursor ranges, column selection
// ═══════════════════════════════════════════════════════════════════════════════

export interface CursorPosition {
  readonly line: number;
  readonly column: number;
}

export interface SelectionRange {
  readonly anchor: CursorPosition;
  readonly active: CursorPosition;
  readonly isReversed: boolean;
}

export interface SelectionState {
  readonly primary: SelectionRange;
  readonly secondary: ReadonlyArray<SelectionRange>;
  readonly columnSelection: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-2 ENCODING STATE — file encoding and line-ending detection
// ═══════════════════════════════════════════════════════════════════════════════

export type FileEncoding =
  | 'utf-8'
  | 'utf-16-le'
  | 'utf-16-be'
  | 'utf-32-le'
  | 'utf-32-be'
  | 'latin-1'
  | 'windows-1252'
  | 'ascii';

export interface EncodingState {
  readonly encoding: FileEncoding;
  readonly lineEnding: LineEnding;
  readonly hasBOM: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-3 FOLDING STATE — fold regions, visibility
// ═══════════════════════════════════════════════════════════════════════════════

export interface FoldRange {
  readonly startLine: number;
  readonly endLine: number;
  readonly level: number;
  readonly isCollapsed: boolean;
  readonly kind?: 'region' | 'imports' | 'comment' | 'language';
}

export interface FoldingState {
  readonly folds: ReadonlyArray<FoldRange>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-4 BRACKETS STATE — auto-close pairs and rainbow matching
// ═══════════════════════════════════════════════════════════════════════════════

export interface BracketsState {
  readonly pairs: ReadonlyArray<BracketPair>;
  readonly autoCloseEnabled: boolean;
  readonly rainbowEnabled: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-5 INDENT STATE — style, size, detection
// ═══════════════════════════════════════════════════════════════════════════════

export type IndentStyle = 'spaces' | 'tabs';

export interface IndentState {
  readonly style: IndentStyle;
  readonly size: number;
  readonly autoDetect: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-6 CODE ACTIONS STATE — quick-fix list, requested range
// ═══════════════════════════════════════════════════════════════════════════════

export interface CodeActionsState {
  readonly actions: ReadonlyArray<CodeAction>;
  readonly requestedRange: Range | undefined;
  readonly isLoading: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-7 INLAY HINTS STATE — type and parameter hints visibility
// ═══════════════════════════════════════════════════════════════════════════════

export interface InlayHintsState {
  readonly hints: ReadonlyArray<InlayHint>;
  readonly enabled: boolean;
  readonly showTypes: boolean;
  readonly showParameters: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-8 VIRTUAL FILESYSTEM STATE — provider mounts, dirty tracking
// ═══════════════════════════════════════════════════════════════════════════════

export type VFSEntryKind = 'file' | 'directory' | 'symlink';

export interface VFSEntry {
  readonly uri: string;
  readonly kind: VFSEntryKind;
  readonly name: string;
  readonly size?: number;
  readonly modifiedAt?: number;
}

export interface VFSState {
  readonly mounts: ReadonlyArray<{ readonly virtualPath: string; readonly providerId: string }>;
  readonly entries: ReadonlyArray<VFSEntry>;
  readonly dirty: ReadonlyArray<string>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-9 WATCHER STATE — file system change event tracking
// ═══════════════════════════════════════════════════════════════════════════════

export type WatcherEventKind = 'created' | 'changed' | 'deleted' | 'renamed';

export interface WatcherEvent {
  readonly path: string;
  readonly kind: WatcherEventKind;
  readonly timestampMs: number;
  readonly newPath?: string;
}

export interface WatcherState {
  readonly watched: ReadonlyArray<string>;
  readonly events: ReadonlyArray<WatcherEvent>;
  readonly paused: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §NEW-10 INCREMENTAL PARSE STATE — edit accumulation, reparse triggers
// ═══════════════════════════════════════════════════════════════════════════════

export interface ParseEdit {
  readonly startLine: number;
  readonly endLine: number;
  readonly newEndLine: number;
  readonly startOffset: number;
  readonly oldEndOffset: number;
  readonly newEndOffset: number;
}

export interface IncrementalParseState {
  readonly pendingEdits: ReadonlyArray<ParseEdit>;
  readonly version: number;
  readonly lastFullParseVersion: number;
  readonly isDirty: boolean;
}
